(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-nvc-diag-root]';
  const GLOBAL_KEY = '__nvcDiagnosticImmobilierLandingV1';
  const IS_COMMONJS_TEST = typeof module === 'object' && module !== null && module.exports;

  if (!IS_COMMONJS_TEST && window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].scan(document);
    return;
  }

  const ADDRESS_ENDPOINT = 'https://data.geopf.fr/geocodage/search/';
  const ADDRESS_MIN_QUERY_LENGTH = 3;
  const ADDRESS_DEBOUNCE_DELAY = 350;
  const ADDRESS_RESULT_LIMIT = 8;
  const TOTAL_SCREENS = 7;
  const QUALIFICATION_PROGRESS_PERCENTAGES = Object.freeze({
    0: 25,
    1: 45,
    2: 60,
    3: 75,
    4: 88,
    5: 100
  });
  const AUTO_ADVANCE_DELAY_MS = 650;
  const AUTO_ADVANCE_REQUIREMENTS = Object.freeze({
    0: Object.freeze(['project']),
    1: Object.freeze(['propertyType', 'surface']),
    2: Object.freeze(['constructionPeriod']),
    3: Object.freeze(['electricityInstallationStatus', 'gasInstallationStatus'])
  });
  const AUTO_ADVANCE_POINTER_TYPES = Object.freeze(['mouse', 'touch', 'pen']);
  const ETAPE_ADRESSE_EVENT_NAME = 'EtapeAdresse';
  const ETAPE_ADRESSE_FBQ_RETRY_DELAYS = Object.freeze([0, 250, 750, 1500]);
  const META_MATCHING_IDENTIFIER_MAX_LENGTH = 512;
  const PROPERTY_SURFACE_STEP_INDEX = 1;
  const ENERGY_STEP_INDEX = 3;
  const ADDRESS_STEP_INDEX = 4;
  const RESULTS_STEP_INDEX = 5;
  const CONTACT_STEP_INDEX = 6;
  const PRODUCTION_EXTRANET_ORIGIN = 'https://espace-client.nouvac.fr';
  const STAGING_EXTRANET_ORIGIN = 'https://staging-espace-client.nouvac.fr';
  const ALLOWED_EXTRANET_ORIGINS = new Set([PRODUCTION_EXTRANET_ORIGIN, STAGING_EXTRANET_ORIGIN]);
  const DIAGNOSTIC_REQUEST_PATH = '/api/public/diagnostic-requests';
  const META_ETAPE_ADRESSE_PATH = '/api/public/marketing/meta-capi/etape-adresse';
  const DIAGNOSTIC_REQUEST_CONTRACT_VERSION = 'diagnostic-request-2026-08-v1';
  const DIAGNOSTIC_FORM_VERSION = 'DIAGNOSTIC_IMMOBILIER_FORM_V2';
  const DIAGNOSTIC_REQUEST_TIMEOUT_MS = 15000;
  const DIAGNOSTIC_HANDOFF_STORAGE_KEY = 'nouvac:diagnostic:v2.1:handoff';
  const DIAGNOSTIC_HANDOFF_VERSION = 'diagnostic-v2.1-handoff-v1';
  const DIAGNOSTIC_HANDOFF_PROVENANCE = 'SHOPIFY_DIAGNOSTIC_FORM';
  const DIAGNOSTIC_BOOKING_PATH = '/pages/commande-service';
  const DIAGNOSTIC_LANDING_FALLBACK_PATH = '/pages/diagnostic-immobilier';
  const DIAGNOSTIC_LANDING_PATHS = new Set([
    '/pages/diagnostic-immobilier',
    '/pages/diagnostic-immobilier/',
    '/pages/diagnostic-immobilier-france',
    '/pages/diagnostic-immobilier-france/'
  ]);
  const TURNSTILE_API_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
  const TURNSTILE_LOAD_TIMEOUT_MS = 10000;
  const TURNSTILE_VERIFICATION_TIMEOUT_MS = 15000;
  const TURNSTILE_FAILURE_MESSAGE = 'Vérification anti-spam échouée. Rechargez la page ou réessayez.';
  const SUBMISSION_AMBIGUOUS_MESSAGE = 'La connexion a été interrompue. Votre demande a peut-être été enregistrée. Réessayez : la même référence de soumission sera utilisée.';
  const SUBMISSION_TECHNICAL_MESSAGE = 'Votre demande ne peut pas être enregistrée pour le moment. Réessayez dans quelques instants.';
  const SUBMISSION_INVALID_MESSAGE = 'Votre demande ne peut pas être confirmée. Vérifiez les informations saisies puis réessayez.';
  const SUBMISSION_MISMATCH_MESSAGE = 'Les diagnostics affichés ne correspondent plus aux informations saisies. Revenez à l’étape précédente puis réessayez.';
  const SUBMISSION_CONFLICT_MESSAGE = 'Cette demande existe déjà avec des informations différentes. Vérifiez les informations saisies avant de réessayer.';
  const SUBMISSION_SUCCESS_TITLE = 'Votre demande a bien été prise en compte';
  const SUBMISSION_MANUAL_SUCCESS_BODY = 'Un conseiller NOUVAC reviendra vers vous afin d’étudier votre demande et de vous proposer la suite adaptée.';
  const CALLBACK_SUCCESS_BODY = 'Un conseiller NOUVAC vous recontacte pour étudier votre demande.';
  const ONLINE_SUCCESS_BODY = 'Votre demande est éligible à la réservation en ligne. Vous pouvez maintenant choisir votre créneau sans ressaisir le questionnaire.';
  const DIAGNOSTIC_SUBMISSION_INTENTS = new Set(['CALLBACK_2H', 'ONLINE_BOOKING']);
  const DIAGNOSTIC_BOOKING_CLASSIFICATIONS = new Set([
    'MANUAL_QUOTE',
    'PRICE_VISIBLE_BOOKING_DISABLED',
    'PRICE_VISIBLE_BOOKING_ENABLED'
  ]);
  const DIAGNOSTIC_CODES = Object.freeze([
    'DPE',
    'ERP',
    'MESURAGE',
    'ELECTRICITE',
    'GAZ',
    'PLOMB',
    'AMIANTE'
  ]);
  const DIAGNOSTIC_CODE_SET = new Set(DIAGNOSTIC_CODES);
  const DIAGNOSTIC_PACK_KEYS = new Set(['PACK_3', 'PACK_4', 'PACK_5', 'PACK_6', 'PACK_7']);
  const UUID_V4_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
  let turnstileApiPromise = null;
  const CONTACT_CIVILITY_LABELS = Object.freeze({
    MONSIEUR: 'Monsieur',
    MADAME: 'Madame',
    SOCIETE: 'Société'
  });

  const REQUIRED_GROUP_MESSAGE = 'Cette information est requise.';
  const INVALID_ADDRESS_MESSAGE = 'Veuillez saisir une adresse valide et complète (Ex. 10 rue de la gare, 67000 Strasbourg).';
  const ADDRESS_UNAVAILABLE_MESSAGE = "Impossible de vérifier l'adresse pour le moment. Réessayez.";
  const ADDRESS_CHECKING_MESSAGE = "Vérification de l'adresse…";
  const ADDRESS_VERIFIED_MESSAGE = 'Adresse vérifiée.';

  // TEST ONLY: replace with a real backend order count or remove before production.
  const ENABLE_POSTAL_FOMO_TEST = true;

  const OVERSEAS_DEPARTMENTS = [
    '971',
    '972',
    '973',
    '974',
    '975',
    '976',
    '977',
    '978',
    '986',
    '987',
    '988'
  ];

  function getDepartmentFromPostalCode(postalCode) {
    if (!postalCode || postalCode.length < 2) return null;
    return postalCode.slice(0, 2);
  }

  function isOverseasPostalCode(postalCode) {
    return OVERSEAS_DEPARTMENTS.includes(String(postalCode || '').slice(0, 3));
  }

  function getParisDateKey() {
    const parts = new Intl.DateTimeFormat('fr-CA', {
      timeZone: 'Europe/Paris',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${values.year}-${values.month}-${values.day}`;
  }

  function getDailyFomoNumber(departmentCode) {
    if (!departmentCode) return 0;

    const seed = `${getParisDateKey()}-${departmentCode}`;
    let hash = 0;
    for (let index = 0; index < seed.length; index += 1) {
      hash = seed.charCodeAt(index) + ((hash << 5) - hash);
    }
    return Math.abs(hash % 6) + 4;
  }

  const DIAGNOSTIC_LABELS = Object.freeze({
    dpe: 'DPE',
    erp: 'ERP',
    mesurage: 'Mesurage',
    electricity: 'Diagnostic électricité',
    gas: 'Diagnostic gaz',
    asbestos: 'Diagnostic amiante',
    lead: 'Diagnostic plomb',
    worksStudy: 'Étude des diagnostics avant travaux',
    worksAsbestos: 'Repérage amiante avant travaux',
    worksLead: 'Repérage plomb avant travaux'
  });

  const CANONICAL_DIAGNOSTIC_ORDER = Object.freeze([
    'dpe',
    'erp',
    'mesurage',
    'electricity',
    'gas',
    'lead',
    'asbestos'
  ]);

  const DIAGNOSTIC_INFORMATION = Object.freeze({
    dpe: Object.freeze({
      label: 'DPE',
      explanation: "Obligatoire pour toute vente et location. Document opposable : sa fiabilité engage votre responsabilité, d'où l'importance d'un technicien certifié.",
      validity: 'Valable 10 ans.',
      ariaLabel: 'En savoir plus sur le DPE'
    }),
    erp: Object.freeze({
      label: 'ERP',
      explanation: "Obligatoire pour toute vente et location. Il recense les risques naturels, miniers et technologiques, la pollution des sols et le radon à l'adresse exacte du bien.",
      validity: 'Valable 6 mois.',
      ariaLabel: 'En savoir plus sur l’ERP'
    }),
    mesurage: Object.freeze({
      label: 'Mesurage',
      ariaLabel: 'En savoir plus sur le mesurage',
      detailsByProject: Object.freeze({
        vente: Object.freeze([
          Object.freeze({ text: 'Bien en copropriété : surface Loi Carrez obligatoire.' }),
          Object.freeze({ text: 'Bien hors copropriété : aucune obligation légale de mesurage. Vous pouvez le faire vous-même, mais en cas d’erreur vous n’êtes pas protégé.' }),
          Object.freeze({
            text: 'NOUVAC vous recommande de confier le mesurage à un professionnel assuré pour sécuriser votre vente et éviter tout litige.',
            emphasized: true
          })
        ]),
        location: Object.freeze([
          Object.freeze({ text: 'Mesurage Loi Boutin obligatoire.' }),
          Object.freeze({ text: 'Le mesurage permet d’indiquer précisément la surface du bien dans le dossier de location.' })
        ])
      })
    }),
    electricity: Object.freeze({
      label: 'Électricité',
      explanation: "Obligatoire lorsque l'installation a plus de 15 ans. Il vérifie sa sécurité et signale les points de non-conformité.",
      validity: 'Valable 3 ans en vente, 6 ans en location.',
      ariaLabel: 'En savoir plus sur le diagnostic électricité'
    }),
    gas: Object.freeze({
      label: 'Gaz',
      explanation: "Obligatoire si le bien est raccordé au gaz et que l'installation a plus de 15 ans. Il contrôle l'étanchéité et la sécurité de l'installation.",
      validity: 'Valable 3 ans en vente, 6 ans en location.',
      ariaLabel: 'En savoir plus sur le diagnostic gaz'
    }),
    lead: Object.freeze({
      label: 'Plomb',
      explanation: 'Obligatoire pour les logements construits avant le 1er janvier 1949. Il recherche la présence de plomb dans les peintures anciennes.',
      validity: 'Valable 1 an en vente si du plomb est détecté, illimité en son absence.',
      ariaLabel: 'En savoir plus sur le diagnostic plomb'
    }),
    asbestos: Object.freeze({
      label: 'Amiante',
      explanation: "Obligatoire pour les biens dont le permis de construire est antérieur au 1er juillet 1997. Il repère les matériaux susceptibles d'en contenir.",
      validity: "Illimité si aucune trace n'est constatée.",
      ariaLabel: 'En savoir plus sur le diagnostic amiante'
    })
  });

  const DEFAULT_DIAGNOSTIC_INFORMATION = Object.freeze({
    explanation: 'Ce diagnostic a été identifié à partir des informations que vous avez fournies. Son périmètre sera confirmé lors du rendez-vous.',
    validity: 'La durée de validité sera précisée selon le diagnostic et la situation du bien.'
  });

  const DIAGNOSTIC_KEY_BY_LABEL = new Map(
    Object.entries(DIAGNOSTIC_LABELS).map(([key, label]) => [label, key])
  );
  const CANONICAL_DIAGNOSTIC_RANK = new Map(
    CANONICAL_DIAGNOSTIC_ORDER.map((key, index) => [key, index])
  );

  const CONFIRMATION_LABELS = Object.freeze({
    electricityStatus: 'Présence et ancienneté de l’installation électrique à confirmer',
    gasAge: 'Présence et ancienneté de l’installation gaz à confirmer',
    constructionPeriod: 'Période de construction à confirmer',
    asbestosLead: 'Amiante et plomb à vérifier selon la date du bien',
    worksScope: 'Nature et périmètre exacts des travaux à confirmer',
    otherWorks: 'Autres repérages à déterminer selon les zones concernées',
    localRules: 'Termites et obligations locales à vérifier selon l’adresse',
    largeSurface: 'Étude personnalisée requise pour une surface supérieure à 200 m²',
    buildingConfiguration: 'Configuration de l’immeuble et mesurages à confirmer',
    professionalPremises: 'Diagnostics et mesurages du local professionnel à confirmer',
    tertiaryUse: 'Diagnostics et mesurages liés à l’usage tertiaire à confirmer',
    otherPropertyType: 'Type de bien et obligations à confirmer'
  });

  const PROPERTY_TYPE_CONFIRMATIONS = Object.freeze({
    immeuble: CONFIRMATION_LABELS.buildingConfiguration,
    'local-professionnel': CONFIRMATION_LABELS.professionalPremises,
    'bureau-tertiaire': CONFIRMATION_LABELS.tertiaryUse,
    autre: CONFIRMATION_LABELS.otherPropertyType
  });

  const ALTERNATIVE_PROPERTY_TYPES = new Set([
    'immeuble',
    'local-professionnel',
    'bureau-tertiaire',
    'autre'
  ]);

  const ALLOWED_DIAGNOSTICS = new Set(Object.values(DIAGNOSTIC_LABELS));
  const ALLOWED_CONFIRMATIONS = new Set(Object.values(CONFIRMATION_LABELS));
  const DIAGNOSTIC_CODE_BY_KEY = Object.freeze({
    dpe: 'DPE',
    erp: 'ERP',
    mesurage: 'MESURAGE',
    electricity: 'ELECTRICITE',
    gas: 'GAZ',
    lead: 'PLOMB',
    asbestos: 'AMIANTE'
  });
  const instances = new WeakMap();

  function normalizeWhitespace(value) {
    return String(value || '').trim().replace(/\s+/g, ' ');
  }

  function getApplicableDiagnosticLabels(state) {
    const diagnostics = [];
    const addDiagnostic = (label) => {
      if (ALLOWED_DIAGNOSTICS.has(label) && !diagnostics.includes(label)) diagnostics.push(label);
    };

    if (['vente', 'location'].includes(state.project)) {
      addDiagnostic(DIAGNOSTIC_LABELS.dpe);
      addDiagnostic(DIAGNOSTIC_LABELS.erp);
      addDiagnostic(DIAGNOSTIC_LABELS.mesurage);
    } else {
      return diagnostics;
    }

    if (['over_15', 'unknown'].includes(state.electricityInstallationStatus)) {
      addDiagnostic(DIAGNOSTIC_LABELS.electricity);
    }
    if (['over_15', 'unknown'].includes(state.gasInstallationStatus)) {
      addDiagnostic(DIAGNOSTIC_LABELS.gas);
    }
    if (['before-1949', 'unknown'].includes(state.constructionPeriod)) {
      addDiagnostic(DIAGNOSTIC_LABELS.lead);
    }
    if (['before-1949', '1949-1997', 'unknown'].includes(state.constructionPeriod)) {
      addDiagnostic(DIAGNOSTIC_LABELS.asbestos);
    }

    return diagnostics;
  }

  function requiresExpertReview(state) {
    return state.project === 'travaux' || ALTERNATIVE_PROPERTY_TYPES.has(state.propertyType);
  }

  function canOfferOnlineBooking(state) {
    return ['vente', 'location'].includes(state.project)
      && ['maison', 'appartement'].includes(state.propertyType)
      && state.surface !== '200-plus';
  }

  function getBrowserAssertion(state) {
    const diagnosticLabels = requiresExpertReview(state) ? [] : getApplicableDiagnosticLabels(state);
    const diagnostics = diagnosticLabels.map((label) => {
      const key = DIAGNOSTIC_KEY_BY_LABEL.get(label);
      return key ? DIAGNOSTIC_CODE_BY_KEY[key] : null;
    }).filter((code) => DIAGNOSTIC_CODE_SET.has(code));

    return {
      diagnostics,
      diagnosticCount: diagnostics.length,
      bookingEligible: canOfferOnlineBooking(state)
    };
  }

  function readAttribution(search) {
    const parameters = new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const readValue = (name) => {
      const value = normalizeWhitespace(parameters.get(name));
      return value
        && Array.from(value).length <= 100
        && !/[\u0000-\u001f\u007f-\u009f]/.test(value)
        ? value
        : undefined;
    };
    const attribution = {
      utmSource: readValue('utm_source'),
      utmMedium: readValue('utm_medium'),
      utmCampaign: readValue('utm_campaign')
    };

    return Object.fromEntries(
      Object.entries(attribution).filter(([, value]) => Boolean(value))
    );
  }

  function readMetaCookie(cookieHeader, cookieName) {
    if (!['_fbp', '_fbc'].includes(cookieName) || typeof cookieHeader !== 'string') return undefined;

    for (const part of cookieHeader.split(';')) {
      const separatorIndex = part.indexOf('=');
      if (separatorIndex < 0 || part.slice(0, separatorIndex).trim() !== cookieName) continue;
      const encodedValue = part.slice(separatorIndex + 1);
      if (!encodedValue) return undefined;

      try {
        return decodeURIComponent(encodedValue);
      } catch (_) {
        return undefined;
      }
    }
    return undefined;
  }

  function buildDiagnosticMarketingMetadata(marketingAllowed, cookieHeader = '') {
    if (marketingAllowed !== true) return { marketing_tracking_allowed: false };

    const fbp = readMetaCookie(cookieHeader, '_fbp');
    const fbc = readMetaCookie(cookieHeader, '_fbc');
    return {
      marketing_tracking_allowed: true,
      ...(fbp ? { fbp } : {}),
      ...(fbc ? { fbc } : {})
    };
  }

  function getDocumentCookie(documentObject) {
    try {
      return typeof documentObject?.cookie === 'string' ? documentObject.cookie : '';
    } catch (_) {
      return '';
    }
  }

  function normalizeDiagnosticLandingPath(pathname) {
    if (typeof pathname !== 'string' || !DIAGNOSTIC_LANDING_PATHS.has(pathname)) {
      return DIAGNOSTIC_LANDING_FALLBACK_PATH;
    }
    return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  }

  function buildDiagnosticEventSourceUrl(locationObject) {
    try {
      const origin = new URL(locationObject?.origin).origin;
      const sourceUrl = new URL(locationObject?.pathname, `${origin}/`);
      if (
        sourceUrl.protocol !== 'https:'
        || sourceUrl.origin !== origin
        || !DIAGNOSTIC_LANDING_PATHS.has(sourceUrl.pathname)
      ) {
        return '';
      }
      return `${sourceUrl.origin}${sourceUrl.pathname}`;
    } catch (_) {
      return '';
    }
  }

  function isTransportableMetaMatchingIdentifier(value) {
    return typeof value === 'string'
      && value.length > 0
      && Array.from(value).length <= META_MATCHING_IDENTIFIER_MAX_LENGTH
      && !/[\u0000-\u001f\u007f-\u009f]/.test(value);
  }

  function postMetaEtapeAdresse({ endpoint, eventId, eventSourceUrl, fbp, fbc, fetchImplementation }) {
    if (!endpoint || !eventId || !eventSourceUrl || typeof fetchImplementation !== 'function') return false;

    try {
      const delivery = fetchImplementation(endpoint, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_id: eventId,
          event_source_url: eventSourceUrl,
          ...(isTransportableMetaMatchingIdentifier(fbp) ? { fbp } : {}),
          ...(isTransportableMetaMatchingIdentifier(fbc) ? { fbc } : {})
        })
      });
      void Promise.resolve(delivery).catch(() => {});
      return true;
    } catch (_) {
      return false;
    }
  }

  function buildDiagnosticRequestBase(state, submissionIntent, search) {
    if (!DIAGNOSTIC_SUBMISSION_INTENTS.has(submissionIntent)) {
      throw new TypeError('Invalid diagnostic submission intent.');
    }

    const address = {
      addressText: normalizeWhitespace(state.addressText),
      addressSource: state.addressSource === 'autocomplete' ? 'AUTOCOMPLETE' : 'MANUAL',
      addressValidationStatus: 'VALID',
      verifiedAddressText: normalizeWhitespace(state.verifiedAddressText),
      postalCode: String(state.postalCode || '').trim(),
      departmentCode: String(state.departmentCode || '').trim(),
      city: normalizeWhitespace(state.city),
      ...(state.addressSource === 'autocomplete' && state.selectedAddressFeatureId
        ? { selectedAddressFeatureId: normalizeWhitespace(state.selectedAddressFeatureId) }
        : {})
    };
    const companyName = normalizeWhitespace(state.contact?.companyName);
    const contact = {
      civility: state.contact?.civility,
      ...(state.contact?.civility === 'SOCIETE' && companyName ? { companyName } : {}),
      firstName: normalizeWhitespace(state.contact?.firstName),
      lastName: normalizeWhitespace(state.contact?.lastName),
      email: normalizeWhitespace(state.contact?.email).toLocaleLowerCase('fr-FR'),
      mobile: String(state.contact?.mobile || '').replace(/\s/g, '')
    };

    return {
      formVersion: DIAGNOSTIC_FORM_VERSION,
      submissionIntent,
      answers: {
        project: state.project,
        propertyType: state.propertyType,
        surface: state.surface,
        constructionPeriod: state.constructionPeriod,
        electricityInstallationStatus: state.electricityInstallationStatus,
        gasInstallationStatus: state.gasInstallationStatus
      },
      address,
      contact,
      browserAssertion: getBrowserAssertion(state),
      attribution: readAttribution(search)
    };
  }

  function stableStringify(value) {
    if (value === null || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;

    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }

  function createUuidV4(cryptoApi) {
    if (!cryptoApi) throw new Error('Secure browser randomness is unavailable.');
    if (typeof cryptoApi.randomUUID === 'function') {
      const uuid = String(cryptoApi.randomUUID()).toLowerCase();
      if (UUID_V4_PATTERN.test(uuid)) return uuid;
    }
    if (typeof cryptoApi.getRandomValues !== 'function') {
      throw new Error('Secure browser randomness is unavailable.');
    }

    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hexadecimal = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hexadecimal.slice(0, 8)}-${hexadecimal.slice(8, 12)}-${hexadecimal.slice(12, 16)}-${hexadecimal.slice(16, 20)}-${hexadecimal.slice(20)}`;
  }

  function ensureSubmissionIdentity(previousIdentity, requestBase, cryptoApi) {
    const identityKey = stableStringify(requestBase);
    if (previousIdentity?.identityKey === identityKey) return previousIdentity;

    return {
      identityKey,
      submissionId: createUuidV4(cryptoApi)
    };
  }

  function buildDiagnosticRequestPayload(
    requestBase,
    submissionId,
    turnstileToken,
    marketingMetadata = { marketing_tracking_allowed: false }
  ) {
    return {
      submissionId,
      ...requestBase,
      ...marketingMetadata,
      turnstileToken
    };
  }

  function parseWebQuoteConsultation(value) {
    if (
      !value || typeof value !== 'object' || Array.isArray(value)
      || value.contractVersion !== 'diagnostic-web-consultation-v1'
      || value.state !== 'PREPARING'
      || typeof value.url !== 'string' || value.url.length > 256
    ) return null;

    try {
      const url = new URL(value.url);
      if (
        url.protocol !== 'https:' || !ALLOWED_EXTRANET_ORIGINS.has(url.origin)
        || url.username || url.password || url.port || url.search
        || url.pathname !== '/public/devis-web'
        || !/^#document=dw1\.[A-Za-z0-9_-]{22}\.[A-Za-z0-9_-]{43}$/.test(url.hash)
        || value.url !== `${url.origin}/public/devis-web${url.hash}`
      ) return null;

      return { contractVersion: value.contractVersion, state: value.state, url: value.url };
    } catch (_) {
      return null;
    }
  }

  function parseDiagnosticSuccessResponse(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const diagnostics = Array.isArray(value.diagnostics) ? value.diagnostics : null;
    const requestReference = typeof value.requestReference === 'string'
      ? value.requestReference.trim()
      : '';
    const packKeyIsValid = value.packKey === null || DIAGNOSTIC_PACK_KEYS.has(value.packKey);
    if (
      value.contractVersion !== DIAGNOSTIC_REQUEST_CONTRACT_VERSION
      || value.formVersion !== DIAGNOSTIC_FORM_VERSION
      || !['CREATED', 'EXISTS'].includes(value.status)
      || requestReference.length === 0
      || requestReference.length > 128
      || !DIAGNOSTIC_SUBMISSION_INTENTS.has(value.submissionIntent)
      || !DIAGNOSTIC_BOOKING_CLASSIFICATIONS.has(value.bookingClassification)
      || !diagnostics
      || diagnostics.some((code) => !DIAGNOSTIC_CODE_SET.has(code))
      || !Number.isInteger(value.diagnosticCount)
      || value.diagnosticCount !== diagnostics.length
      || !packKeyIsValid
      || !Array.isArray(value.blockingReasons)
    ) {
      return null;
    }

    const webQuoteConsultation = parseWebQuoteConsultation(value.webQuoteConsultation);
    return {
      contractVersion: value.contractVersion,
      formVersion: value.formVersion,
      status: value.status,
      requestReference,
      submissionIntent: value.submissionIntent,
      bookingClassification: value.bookingClassification,
      diagnostics: [...diagnostics],
      diagnosticCount: value.diagnosticCount,
      packKey: value.packKey,
      blockingReasons: [...value.blockingReasons],
      ...(webQuoteConsultation ? { webQuoteConsultation } : {})
    };
  }

  class DiagnosticSubmissionError extends Error {
    constructor(kind, code = '', status = 0) {
      super(kind);
      this.name = 'DiagnosticSubmissionError';
      this.kind = kind;
      this.code = code;
      this.status = status;
    }
  }

  async function requestDiagnosticIntake({
    endpoint,
    payload,
    fetchImplementation,
    timeoutMs = DIAGNOSTIC_REQUEST_TIMEOUT_MS
  }) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    let response;
    let responseBody;

    try {
      response = await fetchImplementation(endpoint, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      responseBody = await response.json();
    } catch (_) {
      throw new DiagnosticSubmissionError('ambiguous');
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const code = typeof responseBody?.error?.code === 'string' ? responseBody.error.code : '';
      throw new DiagnosticSubmissionError('server', code, response.status);
    }

    const parsed = parseDiagnosticSuccessResponse(responseBody);
    if (!parsed) throw new DiagnosticSubmissionError('technical', '', response.status);
    return parsed;
  }

  function getSubmissionErrorMessage(error) {
    if (error?.kind === 'ambiguous') return SUBMISSION_AMBIGUOUS_MESSAGE;
    if (error?.code === 'TURNSTILE_REQUIRED' || error?.code === 'TURNSTILE_FAILED') {
      return TURNSTILE_FAILURE_MESSAGE;
    }
    if (error?.code === 'BROWSER_DECISION_MISMATCH') return SUBMISSION_MISMATCH_MESSAGE;
    if (error?.code === 'IDEMPOTENCY_CONFLICT') return SUBMISSION_CONFLICT_MESSAGE;
    if (
      error?.code === 'INVALID_REQUEST'
      || error?.code === 'INVALID_JSON'
      || error?.code === 'UNSUPPORTED_CONTENT_TYPE'
      || error?.code === 'BODY_TOO_LARGE'
    ) {
      return SUBMISSION_INVALID_MESSAGE;
    }
    return SUBMISSION_TECHNICAL_MESSAGE;
  }

  function getSuccessContent(response, sameSessionHandoffReady = true) {
    const { submissionIntent, bookingClassification, blockingReasons = [] } = response;
    const hasWebQuote = Boolean(response.webQuoteConsultation);
    const documentMessage = hasWebQuote ? ' Vous pouvez visualiser votre devis ci-dessous.' : '';
    if (submissionIntent === 'CALLBACK_2H') {
      return {
        title: SUBMISSION_SUCCESS_TITLE,
        body: hasWebQuote
          ? 'Un conseiller NOUVAC vous rappellera. Vous pouvez visualiser votre devis ci-dessous.'
          : CALLBACK_SUCCESS_BODY
      };
    }
    if (bookingClassification !== 'PRICE_VISIBLE_BOOKING_ENABLED') {
      return {
        title: SUBMISSION_SUCCESS_TITLE,
        body: blockingReasons.includes('COVERAGE_UNAVAILABLE')
          ? `La prise de rendez-vous en ligne n’est pas encore disponible à votre adresse.${documentMessage} Un conseiller NOUVAC vous recontacte pour organiser l’intervention.`
          : SUBMISSION_MANUAL_SUCCESS_BODY + documentMessage
      };
    }
    if (!sameSessionHandoffReady) {
      return {
        title: SUBMISSION_SUCCESS_TITLE,
        body: `La réservation en ligne n’a pas pu être ouverte dans ce navigateur.${documentMessage} Un conseiller NOUVAC vous recontacte pour organiser l’intervention.`
      };
    }
    return {
      title: SUBMISSION_SUCCESS_TITLE,
      body: hasWebQuote
        ? 'Vous pouvez visualiser votre devis ci-dessous et choisir votre rendez-vous en ligne.'
        : ONLINE_SUCCESS_BODY
    };
  }

  function getSubmissionIntent(action) {
    if (action === 'quote') return 'CALLBACK_2H';
    if (action === 'booking') return 'ONLINE_BOOKING';
    return null;
  }

  function buildDiagnosticHandoff(response, sourcePath = DIAGNOSTIC_LANDING_FALLBACK_PATH) {
    if (response.bookingClassification !== 'PRICE_VISIBLE_BOOKING_ENABLED') return null;

    return {
      version: DIAGNOSTIC_HANDOFF_VERSION,
      provenance: DIAGNOSTIC_HANDOFF_PROVENANCE,
      contractVersion: response.contractVersion,
      formVersion: response.formVersion,
      requestReference: response.requestReference,
      sourcePath: normalizeDiagnosticLandingPath(sourcePath),
      submissionIntent: response.submissionIntent,
      bookingClassification: response.bookingClassification,
      diagnostics: [...response.diagnostics],
      diagnosticCount: response.diagnosticCount,
      packKey: response.packKey
    };
  }

  function shouldAutoContinueToDiagnosticBooking(response, handoffPersisted) {
    return Boolean(
      handoffPersisted === true &&
      !response?.webQuoteConsultation &&
      ['CREATED', 'EXISTS'].includes(response?.status) &&
      response?.submissionIntent === 'ONLINE_BOOKING' &&
      response?.bookingClassification === 'PRICE_VISIBLE_BOOKING_ENABLED'
    );
  }

  function loadTurnstileApi() {
    if (window.turnstile && typeof window.turnstile.render === 'function') {
      return Promise.resolve(window.turnstile);
    }
    if (turnstileApiPromise) return turnstileApiPromise;

    turnstileApiPromise = new Promise((resolve, reject) => {
      let script = document.querySelector('[data-nvc-diag-turnstile-api]');
      const discardFailedScript = () => {
        if (script?.hasAttribute('data-nvc-diag-turnstile-api')) script.remove();
      };
      const timeoutId = window.setTimeout(() => {
        cleanup();
        discardFailedScript();
        reject(new Error('Turnstile API loading timed out.'));
      }, TURNSTILE_LOAD_TIMEOUT_MS);
      const cleanup = () => {
        window.clearTimeout(timeoutId);
        script?.removeEventListener('load', handleLoad);
        script?.removeEventListener('error', handleError);
      };
      const handleLoad = () => {
        cleanup();
        if (window.turnstile && typeof window.turnstile.render === 'function') {
          resolve(window.turnstile);
          return;
        }
        discardFailedScript();
        reject(new Error('Turnstile API is unavailable.'));
      };
      const handleError = () => {
        cleanup();
        discardFailedScript();
        reject(new Error('Turnstile API loading failed.'));
      };

      const shouldAppendScript = !script;
      if (shouldAppendScript) {
        script = document.createElement('script');
        script.src = TURNSTILE_API_URL;
        script.async = true;
        script.defer = true;
        script.setAttribute('data-nvc-diag-turnstile-api', '');
      }
      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
      if (shouldAppendScript) document.head.appendChild(script);
    }).catch((error) => {
      turnstileApiPromise = null;
      throw error;
    });

    return turnstileApiPromise;
  }

  class DiagnosticLanding {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-nvc-diag-form]');
      this.steps = Array.from(root.querySelectorAll('[data-nvc-diag-step]'))
        .sort((first, second) => Number(first.dataset.nvcDiagStep) - Number(second.dataset.nvcDiagStep));
      this.standardResultNodes = Array.from(this.steps[RESULTS_STEP_INDEX]?.childNodes || []);
      this.finalActionButtons = Array.from(root.querySelectorAll('[data-nvc-diag-success-trigger]'));
      this.certificationHandlers = [];

      this.nodes = {
        progressWrap: root.querySelector('[data-nvc-diag-progress-wrap]'),
        progress: root.querySelector('[data-nvc-diag-progress]'),
        progressBar: root.querySelector('[data-nvc-diag-progress-bar]'),
        progressPercent: root.querySelector('[data-nvc-diag-progress-percent]'),
        live: root.querySelector('[data-nvc-diag-live]'),
        formTitle: root.querySelector('.nvc-diag-form__title'),
        modeSelector: root.querySelector('.nvc-diag-mode-selector'),
        success: root.querySelector('[data-nvc-diag-success]'),
        successHeading: root.querySelector('[data-nvc-diag-success-heading]'),
        successBody: root.querySelector('[data-nvc-diag-success-body]'),
        successQuoteLink: root.querySelector('[data-nvc-diag-success-quote-link]'),
        successBookingLink: root.querySelector('[data-nvc-diag-success-booking-link]'),
        contactGrid: root.querySelector('.nvc-diag-contact__grid'),
        contactErrors: root.querySelector('[data-nvc-diag-contact-errors]'),
        companyFieldTemplate: root.querySelector('[data-nvc-diag-company-field-template]'),
        contactTrustSlot: root.querySelector('[data-nvc-diag-contact-trust-slot]'),
        formTrustHome: root.querySelector('[data-nvc-diag-form-trust-home]'),
        formTrust: root.querySelector('[data-nvc-diag-form-trust]'),
        diagnosticsList: root.querySelector('[data-nvc-diag-diagnostics-list]'),
        resultAnnouncement: root.querySelector('[data-nvc-diag-result-announcement]'),
        expertResultTemplate: root.querySelector('[data-nvc-diag-expert-result-template]'),
        contactConversion: root.querySelector('[data-nvc-diag-contact-conversion]'),
        contactPrivacy: root.querySelector('[data-nvc-diag-contact-privacy]'),
        bookingAction: root.querySelector('[data-nvc-diag-booking-action]'),
        addressWrapper: root.querySelector('[data-nvc-diag-address-wrapper]'),
        addressInput: root.querySelector('[data-nvc-diag-address-input]'),
        addressList: root.querySelector('[data-nvc-diag-address-list]'),
        addressStatus: root.querySelector('[data-nvc-diag-address-status]'),
        addressStatusText: root.querySelector('[data-nvc-diag-address-status-text]'),
        postalFomo: root.querySelector('[data-nvc-diag-postal-fomo]'),
        postalFomoNumber: root.querySelector('[data-nvc-diag-postal-fomo-number]'),
        propertyDisclosure: root.querySelector('[data-nvc-diag-property-disclosure]'),
        propertyAlternatives: root.querySelector('[data-nvc-diag-property-alternatives]'),
        certifications: root.querySelector('[data-nvc-diag-certifications]'),
        turnstileContainer: root.querySelector('[data-nvc-diag-turnstile-container]'),
        turnstileWidget: root.querySelector('[data-nvc-diag-turnstile-widget]'),
        turnstileError: root.querySelector('[data-nvc-diag-turnstile-error]'),
        submissionStatus: root.querySelector('[data-nvc-diag-submission-status]'),
        contactBack: root.querySelector('[data-nvc-diag-step="6"] [data-nvc-diag-back]')
      };
      const configuredExtranetOrigin = normalizeWhitespace(root.dataset.nvcDiagApiOrigin);
      this.extranetOrigin = ALLOWED_EXTRANET_ORIGINS.has(configuredExtranetOrigin)
        ? configuredExtranetOrigin
        : PRODUCTION_EXTRANET_ORIGIN;
      this.diagnosticRequestEndpoint = `${this.extranetOrigin}${DIAGNOSTIC_REQUEST_PATH}`;
      this.metaEtapeAdresseEndpoint = `${this.extranetOrigin}${META_ETAPE_ADRESSE_PATH}`;
      this.turnstileSiteKey = normalizeWhitespace(root.dataset.nvcDiagTurnstileSiteKey);
      this.postalFomoEnabled = root.dataset.nvcDiagPostalFomoEnabled !== 'false';

      this.state = {
        currentStep: 0,
        project: '',
        propertyType: '',
        surface: '',
        constructionPeriod: '',
        electricityInstallationStatus: '',
        gasInstallationStatus: '',
        addressText: '',
        addressSource: null,
        addressValidationStatus: 'idle',
        verifiedAddressText: '',
        postalCode: '',
        departmentCode: '',
        city: '',
        selectedAddressFeatureId: '',
        postalFomoDepartment: '',
        diagnostics: [],
        confirmations: [],
        contact: {
          civility: '',
          companyName: '',
          firstName: '',
          lastName: '',
          email: '',
          mobile: ''
        },
        successVisible: false,
        authoritativeSubmissionResponse: null
      };

      this.addressSuggestions = [];
      this.addressSearchController = null;
      this.addressSearchTimer = null;
      this.addressSearchSequence = 0;
      this.activeAddressIndex = -1;
      this.manualValidationController = null;
      this.manualValidationSequence = 0;
      this.manualValidationPromise = null;
      this.manualValidationAddressText = '';
      this.addressCtaPromise = null;
      this.addressInteracted = false;
      this.suggestionSelectionInProgress = false;
      this.addressBlurTimer = null;
      this.autoAdvanceTimer = null;
      this.autoAdvanceSequence = 0;
      this.autoAdvanceLocked = false;
      this.autoAdvancePointerIntent = null;
      this.autoAdvancePointerCommit = null;
      this.autoAdvanceVisitCounts = new Map();
      this.autoAdvanceManualSteps = new Set();
      this.autoAdvancedSteps = new Set();
      this.submissionIdentity = null;
      this.submissionInFlight = false;
      this.finalActionLabels = new Map(
        this.finalActionButtons.map((button) => [button, button.textContent.trim()])
      );
      this.turnstileWidgetId = null;
      this.turnstileToken = '';
      this.turnstileInitializationPromise = null;
      this.turnstileAcquisitionPromise = null;
      this.turnstileTokenWaiters = new Set();
      this.turnstileInteractionActive = false;
      this.destroyed = false;
      this.etapeAdresseTracking = {
        reached: false,
        eventId: '',
        sent: false,
        refused: false,
        capiAttempted: false,
        retriesStarted: false,
        retryTimers: new Set()
      };

      this.handleRootPointerDown = this.handleRootPointerDown.bind(this);
      this.handleRootPointerCancel = this.handleRootPointerCancel.bind(this);
      this.handleRootClick = this.handleRootClick.bind(this);
      this.handleRootChange = this.handleRootChange.bind(this);
      this.handleRootKeydown = this.handleRootKeydown.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleAddressInput = this.handleAddressInput.bind(this);
      this.handleAddressKeydown = this.handleAddressKeydown.bind(this);
      this.handleAddressBlur = this.handleAddressBlur.bind(this);
      this.handleAddressSuggestionPointerDown = this.handleAddressSuggestionPointerDown.bind(this);
      this.handleContactInput = this.handleContactInput.bind(this);
      this.handleFinalAction = this.handleFinalAction.bind(this);
    }

    init() {
      if (!this.form || this.steps.length !== TOTAL_SCREENS) return false;
      this.destroyed = false;

      this.root.addEventListener('pointerdown', this.handleRootPointerDown);
      this.root.addEventListener('pointercancel', this.handleRootPointerCancel);
      this.root.addEventListener('click', this.handleRootClick);
      this.root.addEventListener('change', this.handleRootChange);
      this.root.addEventListener('keydown', this.handleRootKeydown);
      this.form.addEventListener('submit', this.handleFormSubmit);
      this.nodes.addressInput?.addEventListener('input', this.handleAddressInput);
      this.nodes.addressInput?.addEventListener('keydown', this.handleAddressKeydown);
      this.nodes.addressInput?.addEventListener('blur', this.handleAddressBlur);
      this.nodes.addressList?.addEventListener('pointerdown', this.handleAddressSuggestionPointerDown);
      this.nodes.contactGrid?.addEventListener('input', this.handleContactInput);
      this.finalActionButtons.forEach((button) => button.addEventListener('click', this.handleFinalAction));

      this.setupCertificationLogos();
      this.syncChoiceControls();
      this.syncContactCivilityChoices();
      this.renderContactCompanyField();
      this.syncPropertyDisclosure();
      this.ensureQualificationNextButtonsEnabled();
      this.showStep(0, false);
      return true;
    }

    destroy() {
      this.destroyed = true;
      this.cancelPendingAutoAdvance();
      this.cancelPendingAddressWork();
      this.clearEtapeAdresseRetryTimers();
      window.clearTimeout(this.addressBlurTimer);
      this.root.removeEventListener('pointerdown', this.handleRootPointerDown);
      this.root.removeEventListener('pointercancel', this.handleRootPointerCancel);
      this.root.removeEventListener('click', this.handleRootClick);
      this.root.removeEventListener('change', this.handleRootChange);
      this.root.removeEventListener('keydown', this.handleRootKeydown);
      this.form?.removeEventListener('submit', this.handleFormSubmit);
      this.nodes.addressInput?.removeEventListener('input', this.handleAddressInput);
      this.nodes.addressInput?.removeEventListener('keydown', this.handleAddressKeydown);
      this.nodes.addressInput?.removeEventListener('blur', this.handleAddressBlur);
      this.nodes.addressList?.removeEventListener('pointerdown', this.handleAddressSuggestionPointerDown);
      this.nodes.contactGrid?.removeEventListener('input', this.handleContactInput);
      this.finalActionButtons.forEach((button) => button.removeEventListener('click', this.handleFinalAction));
      this.certificationHandlers.forEach(({ image, handler }) => image.removeEventListener('error', handler));
      this.certificationHandlers = [];
      if (
        this.turnstileWidgetId !== null
        && window.turnstile
        && typeof window.turnstile.remove === 'function'
      ) {
        window.turnstile.remove(this.turnstileWidgetId);
      }
      this.turnstileWidgetId = null;
      this.turnstileToken = '';
      this.turnstileTokenWaiters.forEach((waiter) => waiter(''));
      this.turnstileTokenWaiters.clear();
      this.turnstileAcquisitionPromise = null;
      this.turnstileInteractionActive = false;
      this.syncFormTrustPlacement(-1);
    }

    handleFormSubmit(event) {
      event.preventDefault();
    }

    handleRootChange(event) {
      const choice = event.target.closest('input[type="radio"][data-nvc-diag-choice]');
      if (choice && this.root.contains(choice)) {
        const pointerCommit = this.consumeAutoAdvancePointerIntent(choice, event);
        if (!pointerCommit) this.cancelPendingAutoAdvance();
        const selectionCommitted = this.selectChoice(choice);
        if (selectionCommitted && pointerCommit) this.scheduleAutoAdvance(pointerCommit);
      }
    }

    handleRootKeydown(event) {
      this.cancelPendingAutoAdvance();
      if (event.key !== 'Escape') return;
      const openToggle = this.root.querySelector('[data-nvc-diag-result-toggle][aria-expanded="true"]');
      if (!openToggle) return;
      event.preventDefault();
      this.closeDiagnosticInformation(openToggle);
    }

    handleRootPointerDown(event) {
      this.clearAutoAdvancePointerIntent();
      if (
        !event.isTrusted ||
        event.button !== 0 ||
        !AUTO_ADVANCE_POINTER_TYPES.includes(event.pointerType)
      ) {
        return;
      }

      const currentStep = this.getCurrentStepNode();
      if (!currentStep) return;

      const navigationControl = event.target.closest?.('[data-nvc-diag-next], [data-nvc-diag-back]');
      if (navigationControl && currentStep.contains(navigationControl)) {
        this.cancelPendingAutoAdvance();
        return;
      }

      const choice = this.getChoiceControlFromPointerTarget(event.target);
      if (!choice || !currentStep.contains(choice)) {
        this.cancelPendingAutoAdvance();
        return;
      }
      if (!this.getAutoAdvanceRequiredFields(this.state.currentStep)) {
        this.cancelPendingAutoAdvance();
        return;
      }

      this.cancelPendingAutoAdvance();
      this.autoAdvancePointerIntent = {
        control: choice,
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        stepIndex: this.state.currentStep
      };
    }

    handleRootPointerCancel(event) {
      if (
        this.autoAdvancePointerIntent &&
        this.autoAdvancePointerIntent.pointerId === event.pointerId
      ) {
        this.clearAutoAdvancePointerIntent();
      }
    }

    handleRootClick(event) {
      const addressSuggestion = event.target.closest('[data-nvc-diag-address-suggestion-index]');
      if (addressSuggestion && this.root.contains(addressSuggestion)) {
        event.preventDefault();
        this.selectAddressSuggestion(Number(addressSuggestion.getAttribute('data-nvc-diag-address-suggestion-index')));
        return;
      }

      const choice = event.target.closest('[data-nvc-diag-choice]');
      if (choice && this.root.contains(choice)) {
        if (choice.matches('input[type="radio"]')) return;
        const pointerCommit = this.consumeAutoAdvancePointerIntent(choice, event);
        if (!pointerCommit) this.cancelPendingAutoAdvance();
        const selectionCommitted = this.selectChoice(choice);
        if (selectionCommitted && pointerCommit) this.scheduleAutoAdvance(pointerCommit);
        return;
      }

      const propertyDisclosure = event.target.closest('[data-nvc-diag-property-disclosure]');
      if (propertyDisclosure && this.root.contains(propertyDisclosure)) {
        this.cancelPendingAutoAdvance();
        this.setPropertyDisclosureExpanded(propertyDisclosure.getAttribute('aria-expanded') !== 'true');
        return;
      }

      const resultToggle = event.target.closest('[data-nvc-diag-result-toggle]');
      if (resultToggle && this.root.contains(resultToggle)) {
        this.toggleDiagnosticInformation(resultToggle);
        return;
      }

      const next = event.target.closest('[data-nvc-diag-next]');
      if (next && this.getCurrentStepNode()?.contains(next)) {
        this.cancelPendingAutoAdvance();
        void this.continueFromCurrentStep();
        return;
      }

      const back = event.target.closest('[data-nvc-diag-back]');
      if (back && this.getCurrentStepNode()?.contains(back)) {
        this.cancelPendingAutoAdvance();
        const destinationIndex = Math.max(0, this.state.currentStep - 1);
        this.markAutoAdvanceManual(destinationIndex);
        this.showStep(destinationIndex);
        return;
      }
    }

    selectChoice(control) {
      const field = control.getAttribute('data-nvc-diag-choice-field');
      const value = control.getAttribute('data-nvc-diag-choice-value');
      if (!field || !value || !Object.prototype.hasOwnProperty.call(this.state, field)) return false;

      this.state[field] = value;
      this.clearSubmissionStatus();
      this.root.querySelectorAll(`[data-nvc-diag-choice-field="${field}"]`).forEach((choiceControl) => {
        const selected = choiceControl.getAttribute('data-nvc-diag-choice-value') === value;
        this.syncChoiceControl(choiceControl, selected);
      });
      if (field === 'propertyType') {
        this.setPropertyDisclosureExpanded(ALTERNATIVE_PROPERTY_TYPES.has(value));
      }

      this.clearGroupError(field);
      return true;
    }

    syncChoiceControls() {
      this.root.querySelectorAll('[data-nvc-diag-choice]').forEach((control) => {
        const field = control.getAttribute('data-nvc-diag-choice-field');
        const value = control.getAttribute('data-nvc-diag-choice-value');
        const selected = Boolean(field && value && this.state[field] === value);
        this.syncChoiceControl(control, selected);
      });
    }

    syncChoiceControl(control, selected) {
      if (control.matches('input[type="radio"]')) {
        control.checked = selected;
        control.closest('[data-nvc-diag-property-type], [data-nvc-diag-choice-row], [data-nvc-diag-compact-choice]')?.classList.toggle('is-selected', selected);
        return;
      }

      control.setAttribute('aria-pressed', selected ? 'true' : 'false');
      control.classList.toggle('is-selected', selected);
    }

    ensureQualificationNextButtonsEnabled() {
      this.steps.slice(0, ADDRESS_STEP_INDEX + 1).forEach((step) => {
        const button = step.querySelector('[data-nvc-diag-next]');
        if (!button) return;
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      });
    }

    setPropertyDisclosureExpanded(expanded) {
      if (!this.nodes.propertyDisclosure || !this.nodes.propertyAlternatives) return;
      this.nodes.propertyDisclosure.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.nodes.propertyAlternatives.hidden = !expanded;
    }

    syncPropertyDisclosure() {
      if (ALTERNATIVE_PROPERTY_TYPES.has(this.state.propertyType)) {
        this.setPropertyDisclosureExpanded(true);
      }
    }

    getChoiceControlFromPointerTarget(target) {
      const directChoice = target.closest?.('[data-nvc-diag-choice]');
      const choiceLabel = target.closest?.('label');
      const choice = directChoice || choiceLabel?.querySelector('[data-nvc-diag-choice]');
      return choice && this.root.contains(choice) ? choice : null;
    }

    getAutoAdvanceRequiredFields(stepIndex) {
      return Object.prototype.hasOwnProperty.call(AUTO_ADVANCE_REQUIREMENTS, stepIndex)
        ? AUTO_ADVANCE_REQUIREMENTS[stepIndex]
        : null;
    }

    recordAutoAdvanceVisit(stepIndex) {
      if (!this.getAutoAdvanceRequiredFields(stepIndex)) return;
      const visitCount = (this.autoAdvanceVisitCounts.get(stepIndex) || 0) + 1;
      this.autoAdvanceVisitCounts.set(stepIndex, visitCount);
    }

    markAutoAdvanceManual(stepIndex) {
      if (this.getAutoAdvanceRequiredFields(stepIndex)) {
        this.autoAdvanceManualSteps.add(stepIndex);
      }
    }

    isAutoAdvanceEligible(stepIndex) {
      return Boolean(
        this.getAutoAdvanceRequiredFields(stepIndex) &&
        this.autoAdvanceVisitCounts.get(stepIndex) === 1 &&
        !this.autoAdvanceManualSteps.has(stepIndex) &&
        !this.autoAdvancedSteps.has(stepIndex)
      );
    }

    isAutoAdvanceStepComplete(stepIndex) {
      const requiredFields = this.getAutoAdvanceRequiredFields(stepIndex);
      return Boolean(requiredFields && requiredFields.every((field) => Boolean(this.state[field])));
    }

    clearAutoAdvancePointerIntent() {
      this.autoAdvancePointerIntent = null;
    }

    consumeAutoAdvancePointerIntent(control, event) {
      const pointerIntent = this.autoAdvancePointerIntent;
      this.clearAutoAdvancePointerIntent();
      const validPointerCommit = Boolean(
        event.isTrusted &&
        pointerIntent &&
        pointerIntent.control === control &&
        pointerIntent.stepIndex === this.state.currentStep &&
        this.getCurrentStepNode()?.contains(control)
      );
      return validPointerCommit ? pointerIntent : null;
    }

    cancelPendingAutoAdvance() {
      if (this.autoAdvanceTimer !== null) window.clearTimeout(this.autoAdvanceTimer);
      this.autoAdvanceTimer = null;
      this.autoAdvanceLocked = false;
      this.autoAdvancePointerCommit = null;
      this.autoAdvanceSequence += 1;
      this.clearAutoAdvancePointerIntent();
    }

    scheduleAutoAdvance(pointerCommit) {
      const stepIndex = this.state.currentStep;
      this.cancelPendingAutoAdvance();
      if (
        !pointerCommit ||
        pointerCommit.stepIndex !== stepIndex ||
        !AUTO_ADVANCE_POINTER_TYPES.includes(pointerCommit.pointerType) ||
        !this.isAutoAdvanceEligible(stepIndex) ||
        !this.isAutoAdvanceStepComplete(stepIndex)
      ) {
        return;
      }

      const sequence = this.autoAdvanceSequence;
      this.autoAdvanceLocked = true;
      this.autoAdvancePointerCommit = pointerCommit;
      this.autoAdvanceTimer = window.setTimeout(() => {
        this.autoAdvanceTimer = null;
        if (
          sequence !== this.autoAdvanceSequence ||
          !this.autoAdvanceLocked ||
          this.autoAdvancePointerCommit !== pointerCommit ||
          pointerCommit.stepIndex !== stepIndex ||
          !AUTO_ADVANCE_POINTER_TYPES.includes(pointerCommit.pointerType) ||
          this.state.currentStep !== stepIndex ||
          !this.isAutoAdvanceEligible(stepIndex) ||
          !this.isAutoAdvanceStepComplete(stepIndex)
        ) {
          this.autoAdvanceLocked = false;
          this.autoAdvancePointerCommit = null;
          return;
        }

        this.autoAdvanceLocked = false;
        this.autoAdvancePointerCommit = null;
        this.autoAdvancedSteps.add(stepIndex);
        void this.continueFromCurrentStep();
      }, AUTO_ADVANCE_DELAY_MS);
    }

    getCurrentStepNode() {
      return this.steps[this.state.currentStep] || null;
    }

    showStep(index, moveFocus = true) {
      if (!Number.isInteger(index) || index < 0 || index >= this.steps.length) return;
      this.cancelPendingAutoAdvance();
      const previousStep = this.state.currentStep;

      if (this.state.currentStep === ADDRESS_STEP_INDEX && index !== ADDRESS_STEP_INDEX) {
        window.clearTimeout(this.addressBlurTimer);
        this.addressBlurTimer = null;
        this.cancelPendingAddressWork();
        this.closeAddressSuggestions();
      }
      this.closeDiagnosticInformation();
      this.state.currentStep = index;
      this.recordAutoAdvanceVisit(index);
      this.state.successVisible = false;
      if (index === RESULTS_STEP_INDEX) this.renderResultScreen();
      if (index === CONTACT_STEP_INDEX) {
        this.renderContactActions();
        void this.ensureTurnstileWidget();
      }
      this.steps.forEach((step, stepIndex) => {
        step.hidden = stepIndex !== index;
      });
      if (this.nodes.success) this.nodes.success.hidden = true;
      if (this.nodes.formTitle) this.nodes.formTitle.hidden = false;
      if (this.nodes.modeSelector) this.nodes.modeSelector.hidden = false;
      if (index === PROPERTY_SURFACE_STEP_INDEX) this.syncPropertyDisclosure();
      if (index === ADDRESS_STEP_INDEX) this.restoreAddressPresentation();
      this.syncFormTrustPlacement(index);
      const progressState = this.updateProgress(index);

      const heading = this.steps[index].querySelector('[data-nvc-diag-step-heading]');
      const headingText = this.normalizeWhitespace(heading?.textContent);
      if (this.nodes.live) {
        this.nodes.live.textContent = progressState.showProgress && headingText
          ? headingText
          : '';
      }

      if (moveFocus && heading) {
        window.requestAnimationFrame(() => heading.focus({ preventScroll: false }));
      }

      this.handleCommittedStepTransition(previousStep, index);
    }

    handleCommittedStepTransition(previousStep, nextStep) {
      let marketingConsentReady = null;
      if (nextStep > previousStep && typeof window.NOUVACConsent?.maybeEngagementRegrant === 'function') {
        try {
          marketingConsentReady = window.NOUVACConsent.maybeEngagementRegrant('diagnostic_step');
        } catch (_) {
          marketingConsentReady = null;
        }
      }

      if (previousStep !== ENERGY_STEP_INDEX || nextStep !== ADDRESS_STEP_INDEX) return;
      if (this.etapeAdresseTracking.reached) return;

      this.etapeAdresseTracking.reached = true;
      this.etapeAdresseTracking.eventId = this.createEtapeAdresseEventId();
      if (marketingConsentReady && typeof marketingConsentReady.then === 'function') {
        void Promise.resolve(marketingConsentReady).then(
          () => this.tryEmitEtapeAdresse(),
          () => this.tryEmitEtapeAdresse()
        );
        return;
      }
      this.tryEmitEtapeAdresse();
    }

    createEtapeAdresseEventId() {
      const randomPart = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : Math.random().toString(36).slice(2, 12);
      return `etape_adresse_${Date.now()}_${randomPart}`;
    }

    isEtapeAdresseMarketingAllowed() {
      try {
        return window.NOUVACConsent?.isMarketingAllowed?.() === true;
      } catch (_) {
        return false;
      }
    }

    tryEmitEtapeAdresse() {
      const tracking = this.etapeAdresseTracking;
      if (!tracking.reached || tracking.sent || tracking.refused || tracking.retriesStarted) return;

      if (!this.isEtapeAdresseMarketingAllowed()) {
        tracking.refused = true;
        this.clearEtapeAdresseRetryTimers();
        return;
      }

      this.attemptEtapeAdresseCapi();
      tracking.retriesStarted = true;
      ETAPE_ADRESSE_FBQ_RETRY_DELAYS.forEach((delay) => {
        if (tracking.sent || tracking.refused) return;
        if (delay === 0) {
          this.emitEtapeAdresseIfReady();
          return;
        }

        const timer = window.setTimeout(() => {
          tracking.retryTimers.delete(timer);
          this.emitEtapeAdresseIfReady();
        }, delay);
        tracking.retryTimers.add(timer);
      });
    }

    attemptEtapeAdresseCapi() {
      const tracking = this.etapeAdresseTracking;
      if (tracking.capiAttempted) return;

      tracking.capiAttempted = true;
      const cookieHeader = getDocumentCookie(window.document);
      postMetaEtapeAdresse({
        endpoint: this.metaEtapeAdresseEndpoint,
        eventId: tracking.eventId,
        eventSourceUrl: buildDiagnosticEventSourceUrl(window.location),
        fbp: readMetaCookie(cookieHeader, '_fbp'),
        fbc: readMetaCookie(cookieHeader, '_fbc'),
        fetchImplementation: typeof window.fetch === 'function' ? window.fetch.bind(window) : null
      });
    }

    emitEtapeAdresseIfReady() {
      const tracking = this.etapeAdresseTracking;
      if (!tracking.reached || tracking.sent || tracking.refused) return;

      if (!this.isEtapeAdresseMarketingAllowed()) {
        tracking.refused = true;
        this.clearEtapeAdresseRetryTimers();
        return;
      }
      if (typeof window.fbq !== 'function') return;

      try {
        window.fbq(
          'trackCustom',
          ETAPE_ADRESSE_EVENT_NAME,
          {},
          { eventID: tracking.eventId }
        );
        tracking.sent = true;
        this.clearEtapeAdresseRetryTimers();
      } catch (_) {
        // Meta tracking must never interfere with form navigation.
      }
    }

    clearEtapeAdresseRetryTimers() {
      this.etapeAdresseTracking.retryTimers.forEach((timer) => window.clearTimeout(timer));
      this.etapeAdresseTracking.retryTimers.clear();
    }

    getQualificationProgressPercentage(stepIndex) {
      return Object.prototype.hasOwnProperty.call(QUALIFICATION_PROGRESS_PERCENTAGES, stepIndex)
        ? QUALIFICATION_PROGRESS_PERCENTAGES[stepIndex]
        : null;
    }

    updateProgress(index) {
      const percent = this.getQualificationProgressPercentage(index);
      const showProgress = percent !== null;
      if (this.nodes.progressWrap) this.nodes.progressWrap.hidden = !showProgress;

      if (!showProgress) {
        if (this.nodes.progressPercent) this.nodes.progressPercent.textContent = '';
        if (this.nodes.progressBar) this.nodes.progressBar.style.width = '';
        if (this.nodes.progress) {
          ['role', 'aria-label', 'aria-valuemin', 'aria-valuemax', 'aria-valuenow', 'aria-valuetext']
            .forEach((attribute) => this.nodes.progress.removeAttribute(attribute));
        }
        return { showProgress: false, percent: null };
      }

      if (this.nodes.progressPercent) this.nodes.progressPercent.textContent = `${percent} %`;
      if (this.nodes.progressBar) this.nodes.progressBar.style.width = `${percent}%`;
      if (this.nodes.progress) {
        this.nodes.progress.setAttribute('role', 'progressbar');
        this.nodes.progress.setAttribute('aria-label', 'Progression du questionnaire');
        this.nodes.progress.setAttribute('aria-valuemin', '0');
        this.nodes.progress.setAttribute('aria-valuemax', '100');
        this.nodes.progress.setAttribute('aria-valuenow', String(percent));
        this.nodes.progress.setAttribute('aria-valuetext', `${percent} % terminé`);
      }
      return { showProgress: true, percent };
    }

    async continueFromCurrentStep() {
      const index = this.state.currentStep;

      if (index === ADDRESS_STEP_INDEX) {
        if (this.addressCtaPromise) return;
        const validationPromise = this.validateAddressStep({ focusOnError: true });
        this.addressCtaPromise = validationPromise;
        let addressIsValid = false;
        try {
          addressIsValid = await validationPromise;
        } finally {
          if (this.addressCtaPromise === validationPromise) this.addressCtaPromise = null;
        }
        if (!addressIsValid || this.state.currentStep !== index) return;

        this.computeResults();
        this.showStep(index + 1);
        this.announceResults();
        return;
      }

      if (!this.validateStep(index)) return;
      if (index < TOTAL_SCREENS - 1) {
        this.showStep(index + 1);
      }
    }

    validateStep(index) {
      if (index === 0) {
        return this.validateRequiredGroups(['project']);
      }

      if (index === PROPERTY_SURFACE_STEP_INDEX) {
        return this.validateRequiredGroups(['propertyType', 'surface']);
      }

      if (index === ENERGY_STEP_INDEX) {
        return this.validateRequiredGroups(['electricityInstallationStatus', 'gasInstallationStatus']);
      }

      const fieldByStep = {
        2: 'constructionPeriod'
      };
      const field = fieldByStep[index];
      if (field) {
        return this.validateRequiredGroups([field]);
      }

      return true;
    }

    validateRequiredGroups(fields) {
      const missingFields = fields.filter((field) => !this.state[field]);
      fields.forEach((field) => {
        if (missingFields.includes(field)) this.setGroupError(field, REQUIRED_GROUP_MESSAGE);
        else this.clearGroupError(field);
      });

      if (!missingFields.length) return true;
      this.focusInvalidGroup(missingFields[0]);
      return false;
    }

    setGroupError(field, message) {
      const error = this.root.querySelector(`[data-nvc-diag-group-error="${field}"]`);
      if (!error) return;
      const text = error.querySelector('[data-nvc-diag-error-text]');
      if (text && text.textContent !== message) text.textContent = message;
      error.hidden = false;
      this.setGroupInvalidState(field, true);
    }

    clearGroupError(field) {
      const error = this.root.querySelector(`[data-nvc-diag-group-error="${field}"]`);
      if (error) {
        error.hidden = true;
        const text = error.querySelector('[data-nvc-diag-error-text]');
        if (text) text.textContent = field === 'address' ? INVALID_ADDRESS_MESSAGE : REQUIRED_GROUP_MESSAGE;
      }
      this.setGroupInvalidState(field, false);
    }

    setGroupInvalidState(field, invalid) {
      const group = this.root.querySelector(`[data-nvc-diag-group="${field}"]`);
      const controls = field === 'address'
        ? [this.nodes.addressInput].filter(Boolean)
        : Array.from(this.root.querySelectorAll(`[data-nvc-diag-choice-field="${field}"]`));
      if (invalid) group?.setAttribute('aria-invalid', 'true');
      else group?.removeAttribute('aria-invalid');
      controls.forEach((control) => {
        if (invalid) control.setAttribute('aria-invalid', 'true');
        else control.removeAttribute('aria-invalid');
      });
    }

    focusInvalidGroup(field) {
      const target = field === 'address'
        ? this.nodes.addressInput
        : this.root.querySelector(`[data-nvc-diag-group-heading="${field}"]`);
      window.requestAnimationFrame(() => target?.focus({ preventScroll: false }));
    }

    cancelAddressSearch() {
      window.clearTimeout(this.addressSearchTimer);
      this.addressSearchTimer = null;
      this.addressSearchController?.abort();
      this.addressSearchController = null;
      this.addressSearchSequence += 1;
    }

    cancelManualAddressValidation() {
      this.manualValidationController?.abort();
      this.manualValidationController = null;
      this.manualValidationSequence += 1;
      this.manualValidationPromise = null;
      this.manualValidationAddressText = '';
      if (this.state.addressValidationStatus === 'checking') {
        this.state.addressValidationStatus = 'idle';
      }
    }

    cancelPendingAddressWork() {
      this.cancelAddressSearch();
      this.cancelManualAddressValidation();
    }

    invalidateVerifiedAddress(addressText) {
      this.state.addressText = String(addressText || '');
      this.state.addressSource = null;
      this.state.addressValidationStatus = 'idle';
      this.state.verifiedAddressText = '';
      this.state.postalCode = '';
      this.state.departmentCode = '';
      this.state.city = '';
      this.state.selectedAddressFeatureId = '';
      this.state.postalFomoDepartment = '';
    }

    handleAddressInput() {
      const input = this.nodes.addressInput;
      if (!input) return;

      this.addressInteracted = true;
      this.clearSubmissionStatus();
      this.cancelPendingAddressWork();
      this.closeAddressSuggestions();
      this.invalidateVerifiedAddress(input.value);
      this.clearGroupError('address');
      this.clearAddressStatus();

      const query = this.normalizeWhitespace(input.value);
      if (query.length < ADDRESS_MIN_QUERY_LENGTH) return;

      const requestSequence = this.addressSearchSequence;
      this.setAddressStatus(ADDRESS_CHECKING_MESSAGE);
      this.addressSearchTimer = window.setTimeout(() => {
        this.addressSearchTimer = null;
        void this.fetchAddressSuggestions(query, requestSequence);
      }, ADDRESS_DEBOUNCE_DELAY);
    }

    async fetchAddressSuggestions(query, requestSequence) {
      const controller = new AbortController();
      this.addressSearchController = controller;
      const parameters = new URLSearchParams({
        q: query,
        limit: String(ADDRESS_RESULT_LIMIT)
      });

      try {
        const response = await fetch(`${ADDRESS_ENDPOINT}?${parameters.toString()}`, {
          method: 'GET',
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error('Address search request failed.');

        const payload = await response.json();
        if (
          requestSequence !== this.addressSearchSequence ||
          this.normalizeWhitespace(this.nodes.addressInput?.value) !== query
        ) {
          return;
        }

        this.addressSuggestions = Array.isArray(payload?.features)
          ? payload.features
            .map((feature) => this.parseAddressFeature(feature))
            .filter((address) => this.isUsableAutocompleteAddress(address))
            .slice(0, ADDRESS_RESULT_LIMIT)
          : [];
        this.activeAddressIndex = -1;
        if (this.addressSuggestions.length) this.renderAddressSuggestions();
        else this.closeAddressSuggestions();
        this.clearAddressStatus();
      } catch (error) {
        if (
          error?.name === 'AbortError' ||
          requestSequence !== this.addressSearchSequence ||
          this.normalizeWhitespace(this.nodes.addressInput?.value) !== query
        ) {
          return;
        }
        this.closeAddressSuggestions();
        this.clearAddressStatus();
      } finally {
        if (this.addressSearchController === controller) this.addressSearchController = null;
      }
    }

    parseAddressFeature(feature) {
      const properties = feature?.properties || {};
      const label = this.normalizeWhitespace(properties.label || properties.name);
      const streetNumber = this.normalizeWhitespace(properties.housenumber);
      const street = this.normalizeWhitespace(properties.street);
      const postcode = this.normalizeWhitespace(properties.postcode);
      const city = this.normalizeWhitespace(properties.city || properties.municipality);
      const normalizedLabel = label.toLocaleLowerCase('fr-FR');

      if (
        !streetNumber ||
        street.length < 2 ||
        !label.includes(postcode) ||
        !normalizedLabel.includes(city.toLocaleLowerCase('fr-FR'))
      ) {
        return null;
      }

      const featureId = feature?.id ?? properties.id ?? '';
      return {
        label,
        postcode,
        city,
        featureId: featureId === '' ? '' : String(featureId)
      };
    }

    isUsableAutocompleteAddress(address) {
      return Boolean(
        address &&
        address.label.length >= 3 &&
        /^\d{5}$/.test(address.postcode) &&
        this.hasMinimumUnicodeLetters(address.city, 2)
      );
    }

    renderAddressSuggestions() {
      const list = this.nodes.addressList;
      const input = this.nodes.addressInput;
      if (!list || !input || !this.addressSuggestions.length) {
        this.closeAddressSuggestions();
        return;
      }

      const fragment = document.createDocumentFragment();
      this.addressSuggestions.forEach((address, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        const optionId = `${list.id}-option-${index}`;
        const active = index === this.activeAddressIndex;

        item.id = optionId;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', active ? 'true' : 'false');
        button.type = 'button';
        button.tabIndex = -1;
        button.className = `nvc-diag-address__suggestion${active ? ' is-active' : ''}`;
        button.setAttribute('data-nvc-diag-address-suggestion-index', String(index));
        button.textContent = address.label;
        item.appendChild(button);
        fragment.appendChild(item);
      });

      list.replaceChildren(fragment);
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      if (this.activeAddressIndex >= 0) {
        input.setAttribute('aria-activedescendant', `${list.id}-option-${this.activeAddressIndex}`);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }

    closeAddressSuggestions() {
      if (this.nodes.addressList) {
        this.nodes.addressList.hidden = true;
        this.nodes.addressList.replaceChildren();
      }
      this.nodes.addressInput?.setAttribute('aria-expanded', 'false');
      this.nodes.addressInput?.removeAttribute('aria-activedescendant');
      this.addressSuggestions = [];
      this.activeAddressIndex = -1;
    }

    handleAddressSuggestionPointerDown(event) {
      const suggestion = event.target.closest('[data-nvc-diag-address-suggestion-index]');
      if (!suggestion || !this.nodes.addressList?.contains(suggestion)) return;

      event.preventDefault();
      this.suggestionSelectionInProgress = true;
      window.clearTimeout(this.addressBlurTimer);
      this.addressBlurTimer = null;
      this.selectAddressSuggestion(Number(suggestion.getAttribute('data-nvc-diag-address-suggestion-index')));
      window.setTimeout(() => {
        this.suggestionSelectionInProgress = false;
      }, 0);
    }

    handleAddressKeydown(event) {
      if (event.key === 'Escape') {
        if (!this.nodes.addressList?.hidden) event.preventDefault();
        this.closeAddressSuggestions();
        return;
      }

      const suggestionsAreOpen = !this.nodes.addressList?.hidden && this.addressSuggestions.length > 0;
      if (suggestionsAreOpen && event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeAddressIndex = Math.min(this.activeAddressIndex + 1, this.addressSuggestions.length - 1);
        this.renderAddressSuggestions();
        return;
      }

      if (suggestionsAreOpen && event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeAddressIndex = this.activeAddressIndex <= 0
          ? this.addressSuggestions.length - 1
          : this.activeAddressIndex - 1;
        this.renderAddressSuggestions();
        return;
      }

      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (suggestionsAreOpen && this.activeAddressIndex >= 0) {
        this.selectAddressSuggestion(this.activeAddressIndex);
        return;
      }

      this.closeAddressSuggestions();
      void this.validateAddressStep({ focusOnError: false });
    }

    handleAddressBlur() {
      this.addressInteracted = true;
      window.clearTimeout(this.addressBlurTimer);
      this.addressBlurTimer = window.setTimeout(() => {
        this.addressBlurTimer = null;
        if (this.suggestionSelectionInProgress) return;
        if (this.nodes.addressWrapper?.contains(document.activeElement)) return;
        this.closeAddressSuggestions();
        if (this.state.currentStep !== ADDRESS_STEP_INDEX || !this.addressInteracted) return;

        const currentAddressText = this.normalizeWhitespace(this.nodes.addressInput?.value);
        if (
          this.state.addressValidationStatus === 'valid' &&
          this.state.verifiedAddressText === currentAddressText
        ) {
          this.renderPostalFomo(this.state.postalCode);
          return;
        }
        void this.validateAddressStep({ focusOnError: false });
      }, 0);
    }

    selectAddressSuggestion(index) {
      const address = this.addressSuggestions[index];
      if (!address || !this.isUsableAutocompleteAddress(address)) return false;

      this.cancelPendingAddressWork();
      this.closeAddressSuggestions();
      return this.commitValidatedAddress({
        addressText: address.label,
        source: 'autocomplete',
        postalCode: address.postcode,
        city: address.city,
        featureId: address.featureId
      });
    }

    commitValidatedAddress({ addressText, source, postalCode, city, featureId = '' }) {
      const normalizedAddressText = this.normalizeWhitespace(addressText);
      const normalizedCity = this.normalizeWhitespace(city);
      if (
        !normalizedAddressText ||
        !['autocomplete', 'manual'].includes(source) ||
        !/^\d{5}$/.test(postalCode) ||
        !this.hasMinimumUnicodeLetters(normalizedCity, 2)
      ) {
        return false;
      }

      this.state.addressText = normalizedAddressText;
      this.addressInteracted = true;
      this.state.addressSource = source;
      this.state.addressValidationStatus = 'valid';
      this.state.verifiedAddressText = normalizedAddressText;
      this.state.postalCode = postalCode;
      this.state.departmentCode = postalCode.slice(0, 2);
      this.state.city = normalizedCity;
      this.state.selectedAddressFeatureId = source === 'autocomplete' ? featureId : '';
      if (this.nodes.addressInput && this.nodes.addressInput.value !== normalizedAddressText) {
        this.nodes.addressInput.value = normalizedAddressText;
      }
      this.clearGroupError('address');
      this.renderPostalFomo(postalCode);
      return true;
    }

    parseManualAddress(value) {
      const addressText = this.normalizeWhitespace(value);
      const postcodeMatches = Array.from(addressText.matchAll(/\b\d{5}\b/g));
      if (postcodeMatches.length !== 1) return null;

      const match = postcodeMatches[0];
      const postalCode = match[0];
      const matchIndex = match.index ?? -1;
      if (matchIndex < 0) return null;

      const addressComponent = addressText
        .slice(0, matchIndex)
        .replace(/[,;:\s]+$/u, '')
        .trim();
      const city = addressText
        .slice(matchIndex + postalCode.length)
        .replace(/^[,;:\s–—-]+/u, '')
        .trim();
      if (
        !this.hasMinimumUnicodeLetters(addressComponent, 1) ||
        !this.hasMinimumUnicodeLetters(city, 2)
      ) {
        return null;
      }

      return { addressText, postalCode, city };
    }

    async validateAddressStep({ focusOnError = false } = {}) {
      const input = this.nodes.addressInput;
      const addressText = this.normalizeWhitespace(input?.value);
      this.state.addressText = addressText;
      if (input && input.value !== addressText) input.value = addressText;
      this.closeAddressSuggestions();
      this.cancelAddressSearch();

      if (!addressText) {
        this.cancelManualAddressValidation();
        this.invalidateVerifiedAddress('');
        this.state.addressValidationStatus = 'invalid';
        this.showAddressError(INVALID_ADDRESS_MESSAGE);
        if (focusOnError) this.focusInvalidGroup('address');
        return false;
      }

      if (
        this.state.addressValidationStatus === 'valid' &&
        this.state.verifiedAddressText === addressText &&
        /^\d{5}$/.test(this.state.postalCode)
      ) {
        this.clearGroupError('address');
        this.renderPostalFomo(this.state.postalCode);
        return true;
      }

      const manualAddress = this.parseManualAddress(addressText);
      if (!manualAddress) {
        this.cancelManualAddressValidation();
        this.invalidateVerifiedAddress(addressText);
        this.state.addressValidationStatus = 'invalid';
        this.showAddressError(INVALID_ADDRESS_MESSAGE);
        if (focusOnError) this.focusInvalidGroup('address');
        return false;
      }

      const isValid = await this.verifyManualAddress(manualAddress);
      if (
        !isValid &&
        focusOnError &&
        this.state.currentStep === ADDRESS_STEP_INDEX &&
        this.normalizeWhitespace(this.nodes.addressInput?.value) === addressText &&
        ['invalid', 'unavailable'].includes(this.state.addressValidationStatus)
      ) {
        this.focusInvalidGroup('address');
      }
      return isValid;
    }

    verifyManualAddress(manualAddress) {
      if (
        this.manualValidationPromise &&
        this.manualValidationAddressText === manualAddress.addressText
      ) {
        return this.manualValidationPromise;
      }

      this.cancelManualAddressValidation();
      this.invalidateVerifiedAddress(manualAddress.addressText);
      this.state.addressValidationStatus = 'checking';
      this.clearGroupError('address');
      this.setAddressStatus(ADDRESS_CHECKING_MESSAGE);

      const controller = new AbortController();
      const requestSequence = this.manualValidationSequence;
      const parameters = new URLSearchParams({
        q: manualAddress.postalCode,
        limit: String(ADDRESS_RESULT_LIMIT)
      });
      this.manualValidationController = controller;
      this.manualValidationAddressText = manualAddress.addressText;

      const validationPromise = (async () => {
        try {
          const response = await fetch(`${ADDRESS_ENDPOINT}?${parameters.toString()}`, {
            method: 'GET',
            signal: controller.signal,
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) throw new Error('Address validation request failed.');

          const payload = await response.json();
          if (!this.isCurrentManualValidation(manualAddress, requestSequence)) return false;

          const postalCodeExists = Array.isArray(payload?.features) && payload.features.some((feature) => (
            feature?.properties?.postcode === manualAddress.postalCode
          ));
          if (!postalCodeExists) {
            this.invalidateVerifiedAddress(manualAddress.addressText);
            this.state.addressValidationStatus = 'invalid';
            this.showAddressError(INVALID_ADDRESS_MESSAGE);
            return false;
          }

          return this.commitValidatedAddress({
            addressText: manualAddress.addressText,
            source: 'manual',
            postalCode: manualAddress.postalCode,
            city: manualAddress.city
          });
        } catch (error) {
          if (error?.name === 'AbortError' || !this.isCurrentManualValidation(manualAddress, requestSequence)) {
            return false;
          }
          this.invalidateVerifiedAddress(manualAddress.addressText);
          this.state.addressValidationStatus = 'unavailable';
          this.showAddressError(ADDRESS_UNAVAILABLE_MESSAGE);
          return false;
        } finally {
          if (this.manualValidationController === controller) this.manualValidationController = null;
        }
      })();

      this.manualValidationPromise = validationPromise;
      return validationPromise.finally(() => {
        if (this.manualValidationPromise === validationPromise) {
          this.manualValidationPromise = null;
          this.manualValidationAddressText = '';
        }
      });
    }

    isCurrentManualValidation(manualAddress, requestSequence) {
      return Boolean(
        requestSequence === this.manualValidationSequence &&
        this.state.currentStep === ADDRESS_STEP_INDEX &&
        this.normalizeWhitespace(this.nodes.addressInput?.value) === manualAddress.addressText &&
        this.state.addressText === manualAddress.addressText
      );
    }

    restoreAddressPresentation() {
      const input = this.nodes.addressInput;
      if (input && input.value !== this.state.addressText) input.value = this.state.addressText;
      this.closeAddressSuggestions();
      this.clearGroupError('address');

      const addressText = this.normalizeWhitespace(input?.value);
      if (
        this.state.addressValidationStatus === 'valid' &&
        this.state.verifiedAddressText === addressText &&
        /^\d{5}$/.test(this.state.postalCode)
      ) {
        this.renderPostalFomo(this.state.postalCode);
      } else {
        this.clearAddressStatus();
      }
    }

    renderPostalFomo(postalCode) {
      if (
        this.state.addressValidationStatus !== 'valid' ||
        this.state.verifiedAddressText !== this.normalizeWhitespace(this.nodes.addressInput?.value) ||
        this.state.postalCode !== postalCode ||
        !/^\d{5}$/.test(postalCode)
      ) {
        this.clearAddressStatus();
        return;
      }

      const departmentCode = getDepartmentFromPostalCode(postalCode);
      this.state.departmentCode = departmentCode || '';
      if (!ENABLE_POSTAL_FOMO_TEST || !this.postalFomoEnabled || isOverseasPostalCode(postalCode)) {
        this.state.postalFomoDepartment = '';
        this.setAddressStatus(ADDRESS_VERIFIED_MESSAGE);
        return;
      }

      const fomoNumber = getDailyFomoNumber(departmentCode);
      this.state.postalFomoDepartment = departmentCode || '';
      if (!fomoNumber) {
        this.setAddressStatus(ADDRESS_VERIFIED_MESSAGE);
        return;
      }

      if (this.nodes.addressStatusText) {
        if (this.nodes.addressStatusText.textContent) this.nodes.addressStatusText.textContent = '';
        if (!this.nodes.addressStatusText.hidden) this.nodes.addressStatusText.hidden = true;
      }
      if (
        this.nodes.postalFomoNumber &&
        this.nodes.postalFomoNumber.textContent !== String(fomoNumber)
      ) {
        this.nodes.postalFomoNumber.textContent = String(fomoNumber);
      }
      if (this.nodes.postalFomo?.hidden) this.nodes.postalFomo.hidden = false;
      if (this.nodes.addressStatus?.hidden) this.nodes.addressStatus.hidden = false;
    }

    showAddressError(message) {
      this.clearAddressStatus();
      this.setGroupError('address', message);
    }

    setAddressStatus(message) {
      if (this.nodes.postalFomo) this.nodes.postalFomo.hidden = true;
      if (this.nodes.postalFomoNumber) this.nodes.postalFomoNumber.textContent = '';
      if (this.nodes.addressStatusText) {
        if (this.nodes.addressStatusText.textContent !== message) {
          this.nodes.addressStatusText.textContent = message;
        }
        this.nodes.addressStatusText.hidden = !message;
      }
      if (this.nodes.addressStatus) this.nodes.addressStatus.hidden = !message;
    }

    clearAddressStatus() {
      if (this.nodes.addressStatusText) {
        this.nodes.addressStatusText.textContent = '';
        this.nodes.addressStatusText.hidden = true;
      }
      if (this.nodes.postalFomo) this.nodes.postalFomo.hidden = true;
      if (this.nodes.postalFomoNumber) this.nodes.postalFomoNumber.textContent = '';
      if (this.nodes.addressStatus) this.nodes.addressStatus.hidden = true;
    }

    hasMinimumUnicodeLetters(value, minimum) {
      const matches = String(value || '').match(/\p{L}/gu);
      return Boolean(matches && matches.length >= minimum);
    }

    getApplicableDiagnostics(state) {
      return getApplicableDiagnosticLabels(state);
    }

    computeResults() {
      const expertReviewRequired = this.requiresExpertReview();
      const diagnostics = expertReviewRequired ? [] : this.getApplicableDiagnostics(this.state);
      const confirmations = [];
      const addConfirmation = (label) => {
        if (ALLOWED_CONFIRMATIONS.has(label) && !confirmations.includes(label)) confirmations.push(label);
      };

      if (expertReviewRequired) {
        if (this.state.project === 'travaux') {
          addConfirmation(CONFIRMATION_LABELS.worksScope);
          addConfirmation(CONFIRMATION_LABELS.otherWorks);
        }

        const propertyTypeConfirmation = PROPERTY_TYPE_CONFIRMATIONS[this.state.propertyType];
        if (propertyTypeConfirmation) addConfirmation(propertyTypeConfirmation);
      }

      if (this.state.electricityInstallationStatus === 'unknown') {
        addConfirmation(CONFIRMATION_LABELS.electricityStatus);
      }
      if (this.state.gasInstallationStatus === 'unknown') {
        addConfirmation(CONFIRMATION_LABELS.gasAge);
      }
      if (this.state.constructionPeriod === 'unknown') {
        addConfirmation(CONFIRMATION_LABELS.constructionPeriod);
        addConfirmation(CONFIRMATION_LABELS.asbestosLead);
      }
      addConfirmation(CONFIRMATION_LABELS.localRules);
      if (this.state.surface === '200-plus') addConfirmation(CONFIRMATION_LABELS.largeSurface);

      this.state.diagnostics = diagnostics;
      this.state.confirmations = confirmations;
    }

    requiresExpertReview() {
      return requiresExpertReview(this.state);
    }

    renderResultScreen() {
      const resultStep = this.steps[RESULTS_STEP_INDEX];
      if (!resultStep) return;
      const expertReviewRequired = this.requiresExpertReview();

      if (expertReviewRequired) {
        const expertResult = this.nodes.expertResultTemplate?.content.cloneNode(true);
        if (expertResult) resultStep.replaceChildren(expertResult);
      } else {
        const standardResultIsRendered = this.standardResultNodes.every((node) => node.parentNode === resultStep);
        if (!standardResultIsRendered) resultStep.replaceChildren(...this.standardResultNodes);
      }

      this.nodes.diagnosticsList = resultStep.querySelector('[data-nvc-diag-diagnostics-list]');
      this.nodes.resultAnnouncement = resultStep.querySelector('[data-nvc-diag-result-announcement]');
      if (!expertReviewRequired) this.renderDiagnosticList(this.state.diagnostics);
    }

    canOfferOnlineBooking() {
      return canOfferOnlineBooking(this.state);
    }

    renderContactActions() {
      const conversion = this.nodes.contactConversion;
      const privacy = this.nodes.contactPrivacy;
      const bookingAction = this.nodes.bookingAction;
      if (!conversion || !privacy || !bookingAction) return;

      if (!this.canOfferOnlineBooking()) {
        bookingAction.remove();
        return;
      }

      if (bookingAction.parentNode !== conversion) privacy.after(bookingAction);
    }

    announceResults() {
      const resultAnnouncement = this.nodes.resultAnnouncement;
      if (resultAnnouncement) {
        resultAnnouncement.textContent = '';
        window.requestAnimationFrame(() => {
          if (this.nodes.resultAnnouncement !== resultAnnouncement) return;
          if (this.requiresExpertReview()) {
            resultAnnouncement.textContent = 'Votre demande nécessite une expertise approfondie. Veuillez compléter vos informations à l’étape suivante afin d’être rappelé par un conseiller NOUVAC.';
            return;
          }
          const diagnosticsCount = this.state.diagnostics.length;
          resultAnnouncement.textContent = `${diagnosticsCount} diagnostic${diagnosticsCount > 1 ? 's' : ''} identifié${diagnosticsCount > 1 ? 's' : ''} pour votre bien.`;
        });
      }
    }

    normalizeDiagnosticResults(labels) {
      const seen = new Set();
      const normalized = [];

      labels.forEach((sourceLabel, sourceIndex) => {
        const mappedKey = DIAGNOSTIC_KEY_BY_LABEL.get(sourceLabel);
        const key = mappedKey || `other-${sourceIndex}`;
        const deduplicationKey = mappedKey ? `key:${key}` : `label:${sourceLabel}`;
        if (seen.has(deduplicationKey)) return;
        seen.add(deduplicationKey);

        const information = DIAGNOSTIC_INFORMATION[key] || DEFAULT_DIAGNOSTIC_INFORMATION;
        normalized.push({
          key,
          sourceIndex,
          rank: CANONICAL_DIAGNOSTIC_RANK.has(key)
            ? CANONICAL_DIAGNOSTIC_RANK.get(key)
            : Number.POSITIVE_INFINITY,
          label: information.label || sourceLabel,
          details: this.getDiagnosticDetails(information),
          ariaLabel: information.ariaLabel || `En savoir plus sur ${sourceLabel}`
        });
      });

      return normalized.sort((first, second) => {
        if (first.rank !== second.rank) return first.rank - second.rank;
        return first.sourceIndex - second.sourceIndex;
      });
    }

    getDiagnosticDetails(information) {
      const projectDetails = information.detailsByProject?.[this.state.project];
      if (Array.isArray(projectDetails)) return projectDetails;

      return [
        { text: information.explanation, emphasized: false },
        { text: information.validity, emphasized: true }
      ].filter((detail) => Boolean(detail.text));
    }

    renderDiagnosticList(labels) {
      const list = this.nodes.diagnosticsList;
      if (!list) return;
      const fragment = document.createDocumentFragment();

      this.normalizeDiagnosticResults(labels).forEach((diagnostic) => {
        const sectionId = String(this.root.dataset.nvcDiagSectionId || 'section')
          .replace(/[^a-zA-Z0-9_-]/g, '-');
        const controlId = `nvc-diag-result-control-${sectionId}-${diagnostic.key}-${diagnostic.sourceIndex}`;
        const panelId = `nvc-diag-result-panel-${sectionId}-${diagnostic.key}-${diagnostic.sourceIndex}`;
        const item = document.createElement('li');
        const row = document.createElement('div');
        const check = document.createElement('span');
        const checkSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        const checkPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        const label = document.createElement('span');
        const toggle = document.createElement('button');
        const panel = document.createElement('div');

        item.className = 'nvc-diag-result-item';
        item.setAttribute('data-nvc-diag-diagnostic-key', diagnostic.key);
        row.className = 'nvc-diag-result-item__row';
        check.className = 'nvc-diag-result-item__check';
        check.setAttribute('aria-hidden', 'true');
        checkSvg.setAttribute('viewBox', '0 0 24 24');
        checkSvg.setAttribute('fill', 'none');
        checkSvg.setAttribute('aria-hidden', 'true');
        checkSvg.setAttribute('focusable', 'false');
        checkPath.setAttribute('d', 'M5 12.5 9.2 17 19 7');
        checkPath.setAttribute('stroke', 'currentColor');
        checkPath.setAttribute('stroke-width', '2.4');
        checkPath.setAttribute('stroke-linecap', 'round');
        checkPath.setAttribute('stroke-linejoin', 'round');
        checkSvg.appendChild(checkPath);
        check.appendChild(checkSvg);

        label.className = 'nvc-diag-result-item__label';
        label.textContent = diagnostic.label;

        toggle.id = controlId;
        toggle.className = 'nvc-diag-result-item__toggle';
        toggle.type = 'button';
        toggle.textContent = '?';
        toggle.setAttribute('aria-label', diagnostic.ariaLabel);
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', panelId);
        toggle.setAttribute('data-nvc-diag-result-toggle', '');

        panel.id = panelId;
        panel.className = 'nvc-diag-result-item__panel';
        panel.hidden = true;
        panel.setAttribute('aria-labelledby', controlId);
        panel.setAttribute('data-nvc-diag-result-panel', '');
        diagnostic.details.forEach((detail) => {
          const paragraph = document.createElement('p');
          paragraph.className = detail.emphasized
            ? 'nvc-diag-result-item__validity'
            : 'nvc-diag-result-item__explanation';
          paragraph.textContent = detail.text;
          panel.appendChild(paragraph);
        });

        row.append(check, label, toggle);
        item.append(row, panel);
        fragment.appendChild(item);
      });

      list.replaceChildren(fragment);
    }

    toggleDiagnosticInformation(toggle) {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        this.closeDiagnosticInformation(toggle);
        return;
      }

      this.closeDiagnosticInformation();
      const panelId = toggle.getAttribute('aria-controls');
      const panel = panelId
        ? this.root.querySelector(`[data-nvc-diag-result-panel][id="${panelId}"]`)
        : null;
      if (!panel) return;

      toggle.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      toggle.closest('.nvc-diag-result-item')?.classList.add('is-open');
    }

    closeDiagnosticInformation(toggle = null) {
      const openToggle = toggle || this.root.querySelector('[data-nvc-diag-result-toggle][aria-expanded="true"]');
      if (!openToggle) return;

      const panelId = openToggle.getAttribute('aria-controls');
      const panel = panelId
        ? this.root.querySelector(`[data-nvc-diag-result-panel][id="${panelId}"]`)
        : null;
      openToggle.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      openToggle.closest('.nvc-diag-result-item')?.classList.remove('is-open');
    }

    handleContactInput(event) {
      const input = event.target.closest('[data-nvc-diag-contact-field]');
      if (!input || !this.nodes.contactGrid?.contains(input)) return;
      const field = input.getAttribute('data-nvc-diag-contact-field');
      if (!field || !Object.prototype.hasOwnProperty.call(this.state.contact, field)) return;

      if (field === 'civility') {
        if (!input.checked || !this.isValidCivility(input.value)) return;
        this.state.contact.civility = input.value;
        this.clearSubmissionStatus();
        this.syncContactCivilityChoices();
        this.renderContactCompanyField();
        this.clearFieldError(field);
        return;
      }

      if (field === 'mobile') {
        this.state.contact.mobile = this.sanitizeTelephoneInput(input);
        this.clearSubmissionStatus();
        if (this.isValidTelephone(this.state.contact.mobile)) this.clearFieldError(field);
        return;
      }

      this.state.contact[field] = input.value;
      this.clearSubmissionStatus();
      if (field === 'companyName' && !this.normalizeWhitespace(this.state.contact.companyName)) return;
      this.clearFieldError(field);
    }

    syncContactCivilityChoices() {
      this.root.querySelectorAll('[data-nvc-diag-contact-field="civility"]').forEach((input) => {
        input.closest('[data-nvc-diag-contact-choice]')?.classList.toggle('is-selected', input.checked);
      });
    }

    renderContactCompanyField() {
      const currentField = this.nodes.contactGrid?.querySelector('[data-nvc-diag-company-field]');
      if (this.state.contact.civility === 'SOCIETE') {
        if (currentField || !this.nodes.companyFieldTemplate?.content) return;
        const fragment = this.nodes.companyFieldTemplate.content.cloneNode(true);
        const input = fragment.querySelector('[data-nvc-diag-contact-field="companyName"]');
        if (input) input.value = this.state.contact.companyName;
        this.nodes.companyFieldTemplate.after(fragment);
        return;
      }

      this.clearFieldError('companyName');
      this.state.contact.companyName = '';
      currentField?.remove();
    }

    sanitizeTelephoneValue(value) {
      const allowedValue = String(value || '').replace(/[^\d ]/g, '');
      let digits = '';
      let visibleValue = '';

      for (const character of allowedValue) {
        if (character >= '0' && character <= '9') {
          if (digits.length >= 10) continue;
          digits += character;
          visibleValue += character;
          continue;
        }

        if (digits.length && digits.length < 10 && !visibleValue.endsWith(' ')) {
          visibleValue += ' ';
        }
      }

      return { digits, visibleValue };
    }

    sanitizeTelephoneInput(input) {
      const rawValue = String(input?.value || '');
      const selectionStart = input?.selectionStart;
      const selectionEnd = input?.selectionEnd;
      const sanitized = this.sanitizeTelephoneValue(rawValue);

      if (input && input.value !== sanitized.visibleValue) {
        const nextSelectionStart = typeof selectionStart === 'number'
          ? this.sanitizeTelephoneValue(rawValue.slice(0, selectionStart)).visibleValue.length
          : null;
        const nextSelectionEnd = typeof selectionEnd === 'number'
          ? this.sanitizeTelephoneValue(rawValue.slice(0, selectionEnd)).visibleValue.length
          : null;

        input.value = sanitized.visibleValue;
        if (
          document.activeElement === input &&
          nextSelectionStart !== null &&
          nextSelectionEnd !== null &&
          typeof input.setSelectionRange === 'function'
        ) {
          input.setSelectionRange(
            Math.min(nextSelectionStart, sanitized.visibleValue.length),
            Math.min(nextSelectionEnd, sanitized.visibleValue.length)
          );
        }
      }

      return sanitized.digits;
    }

    async ensureTurnstileWidget() {
      if (this.turnstileWidgetId !== null) return true;
      if (this.turnstileInitializationPromise) return this.turnstileInitializationPromise;
      if (!this.nodes.turnstileWidget || !this.turnstileSiteKey) {
        this.showTurnstileError(TURNSTILE_FAILURE_MESSAGE);
        return false;
      }

      this.turnstileInitializationPromise = (async () => {
        try {
          const turnstile = await loadTurnstileApi();
          if (this.destroyed || this.turnstileWidgetId !== null) return !this.destroyed;
          const widgetId = turnstile.render(this.nodes.turnstileWidget, {
            sitekey: this.turnstileSiteKey,
            theme: 'auto',
            language: 'fr',
            size: 'flexible',
            appearance: 'interaction-only',
            retry: 'auto',
            'refresh-expired': 'auto',
            'response-field': false,
            'before-interactive-callback': () => {
              this.clearTurnstileError();
              this.turnstileInteractionActive = true;
              this.setTurnstilePresentation('interactive');
            },
            'after-interactive-callback': () => {
              this.turnstileInteractionActive = false;
              this.syncTurnstilePresentation();
            },
            callback: (token) => {
              const normalizedToken = String(token || '').trim();
              this.turnstileToken = normalizedToken.length <= 2048 ? normalizedToken : '';
              if (this.turnstileToken) {
                this.clearTurnstileError();
                this.resolveTurnstileTokenWaiters(this.turnstileToken);
              }
            },
            'expired-callback': () => {
              const submissionWaiting = this.turnstileTokenWaiters.size > 0;
              this.turnstileToken = '';
              this.resolveTurnstileTokenWaiters('');
              if (submissionWaiting) this.showTurnstileError(TURNSTILE_FAILURE_MESSAGE);
              else this.syncTurnstilePresentation();
            },
            'timeout-callback': () => {
              this.turnstileToken = '';
              this.resolveTurnstileTokenWaiters('');
              this.showTurnstileError(TURNSTILE_FAILURE_MESSAGE);
            },
            'error-callback': () => {
              this.turnstileToken = '';
              this.resolveTurnstileTokenWaiters('');
              this.showTurnstileError(TURNSTILE_FAILURE_MESSAGE);
            }
          });
          if (widgetId === null || widgetId === undefined) {
            throw new Error('Turnstile widget initialization failed.');
          }
          this.turnstileWidgetId = widgetId;
          return true;
        } catch (_) {
          if (!this.destroyed) this.showTurnstileError(TURNSTILE_FAILURE_MESSAGE);
          return false;
        } finally {
          this.turnstileInitializationPromise = null;
        }
      })();

      return this.turnstileInitializationPromise;
    }

    setTurnstilePresentation(state) {
      if (!this.nodes.turnstileContainer) return;
      this.nodes.turnstileContainer.dataset.nvcDiagTurnstileState = state;
    }

    syncTurnstilePresentation() {
      const hasError = Boolean(this.nodes.turnstileError && !this.nodes.turnstileError.hidden);
      this.setTurnstilePresentation(
        this.turnstileInteractionActive ? 'interactive' : hasError ? 'error' : 'compact'
      );
    }

    resolveTurnstileTokenWaiters(token) {
      const waiters = Array.from(this.turnstileTokenWaiters);
      this.turnstileTokenWaiters.clear();
      waiters.forEach((waiter) => waiter(token));
    }

    waitForTurnstileToken(timeoutMs = TURNSTILE_VERIFICATION_TIMEOUT_MS) {
      if (this.turnstileToken) return Promise.resolve(this.turnstileToken);
      return new Promise((resolve) => {
        let settled = false;
        const finish = (token) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          this.turnstileTokenWaiters.delete(finish);
          resolve(token);
        };
        const timeoutId = window.setTimeout(() => finish(''), timeoutMs);
        this.turnstileTokenWaiters.add(finish);
        if (this.turnstileToken) finish(this.turnstileToken);
      });
    }

    resetTurnstile() {
      this.turnstileToken = '';
      this.resolveTurnstileTokenWaiters('');
      this.turnstileInteractionActive = false;
      this.clearTurnstileError();
      if (
        this.turnstileWidgetId !== null
        && window.turnstile
        && typeof window.turnstile.reset === 'function'
      ) {
        window.turnstile.reset(this.turnstileWidgetId);
      }
    }

    showTurnstileError(message) {
      if (!this.nodes.turnstileError) return;
      this.nodes.turnstileError.textContent = message;
      this.nodes.turnstileError.hidden = false;
      this.setTurnstilePresentation('error');
    }

    clearTurnstileError() {
      if (!this.nodes.turnstileError) return;
      this.nodes.turnstileError.textContent = '';
      this.nodes.turnstileError.hidden = true;
      this.syncTurnstilePresentation();
    }

    showSubmissionStatus(message, state = 'error') {
      if (!this.nodes.submissionStatus) return;
      this.nodes.submissionStatus.textContent = message;
      this.nodes.submissionStatus.dataset.nvcDiagSubmissionState = state;
      this.nodes.submissionStatus.hidden = !message;
      if (message && state === 'error') this.nodes.submissionStatus.focus({ preventScroll: false });
    }

    clearSubmissionStatus() {
      if (this.submissionInFlight || !this.nodes.submissionStatus) return;
      this.nodes.submissionStatus.textContent = '';
      delete this.nodes.submissionStatus.dataset.nvcDiagSubmissionState;
      this.nodes.submissionStatus.hidden = true;
    }

    setSubmissionBusy(busy, initiatingButton = null) {
      this.submissionInFlight = busy;
      this.finalActionButtons.forEach((button) => {
        button.disabled = busy;
        button.setAttribute('aria-busy', busy ? 'true' : 'false');
        button.classList.toggle('is-loading', busy && button === initiatingButton);
        button.textContent = busy && button === initiatingButton
          ? 'Enregistrement…'
          : this.finalActionLabels.get(button) || button.textContent;
      });

      const lockableControls = [
        ...this.root.querySelectorAll('[data-nvc-diag-step="6"] [data-nvc-diag-contact-field]'),
        this.nodes.contactBack
      ].filter(Boolean);
      if (busy) {
        this.submissionLockedControls = new Map(
          lockableControls.map((control) => [control, control.disabled])
        );
        lockableControls.forEach((control) => {
          control.disabled = true;
        });
      } else {
        this.submissionLockedControls?.forEach((wasDisabled, control) => {
          if (control.isConnected) control.disabled = wasDisabled;
        });
        this.submissionLockedControls = null;
      }
    }

    persistDiagnosticHandoff(response) {
      const handoff = buildDiagnosticHandoff(response, window.location?.pathname);
      try {
        if (handoff) {
          window.sessionStorage.setItem(DIAGNOSTIC_HANDOFF_STORAGE_KEY, JSON.stringify(handoff));
          return true;
        } else {
          window.sessionStorage.removeItem(DIAGNOSTIC_HANDOFF_STORAGE_KEY);
        }
      } catch (_) {
        // Session storage is a convenience for the next branch, never the durable authority.
      }
      return false;
    }

    showServerSuccess(response, sameSessionHandoffReady = this.state.sameSessionHandoffReady === true) {
      const content = getSuccessContent(response, sameSessionHandoffReady);
      if (this.nodes.successHeading) this.nodes.successHeading.textContent = content.title;
      if (this.nodes.successBody) this.nodes.successBody.textContent = content.body;
      if (this.nodes.successQuoteLink) {
        this.nodes.successQuoteLink.hidden = !response.webQuoteConsultation;
        if (response.webQuoteConsultation) {
          this.nodes.successQuoteLink.href = response.webQuoteConsultation.url;
        } else {
          this.nodes.successQuoteLink.removeAttribute('href');
        }
      }
      if (this.nodes.successBookingLink) {
        this.nodes.successBookingLink.hidden = !(
          sameSessionHandoffReady &&
          response.submissionIntent === 'ONLINE_BOOKING' &&
          response.bookingClassification === 'PRICE_VISIBLE_BOOKING_ENABLED'
        );
      }
      this.state.successVisible = true;
      this.steps.forEach((step) => {
        step.hidden = true;
      });
      this.syncFormTrustPlacement(-1);
      if (this.nodes.progressWrap) this.nodes.progressWrap.hidden = true;
      if (this.nodes.formTitle) this.nodes.formTitle.hidden = true;
      if (this.nodes.modeSelector) this.nodes.modeSelector.hidden = true;
      if (this.nodes.success) this.nodes.success.hidden = false;
      if (this.nodes.live) this.nodes.live.textContent = content.title;
      window.requestAnimationFrame(() => this.nodes.successHeading?.focus({ preventScroll: false }));
    }

    async handleFinalAction(event) {
      event.preventDefault();
      if (this.submissionInFlight) return;
      const action = event.currentTarget.getAttribute('data-nvc-diag-contact-action');
      const submissionIntent = getSubmissionIntent(action);
      if (!submissionIntent) return;
      if (!this.validateContact()) return;
      this.clearSubmissionStatus();

      const requestBase = buildDiagnosticRequestBase(
        this.state,
        submissionIntent,
        window.location.search
      );
      try {
        this.submissionIdentity = ensureSubmissionIdentity(
          this.submissionIdentity,
          requestBase,
          window.crypto
        );
      } catch (_) {
        this.showSubmissionStatus(SUBMISSION_TECHNICAL_MESSAGE);
        return;
      }

      if (!this.turnstileToken) {
        if (this.turnstileAcquisitionPromise) return;
        this.turnstileAcquisitionPromise = (async () => {
          const widgetReady = await this.ensureTurnstileWidget();
          if (!widgetReady || this.destroyed) return '';
          return this.turnstileToken || this.waitForTurnstileToken();
        })();
        try {
          await this.turnstileAcquisitionPromise;
        } finally {
          this.turnstileAcquisitionPromise = null;
        }
        if (this.destroyed || this.submissionInFlight) return;
        if (!this.turnstileToken) {
          this.showTurnstileError(TURNSTILE_FAILURE_MESSAGE);
          this.nodes.turnstileContainer?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          return;
        }
      }

      const turnstileToken = this.turnstileToken;
      this.turnstileToken = '';
      const marketingMetadata = buildDiagnosticMarketingMetadata(
        this.isEtapeAdresseMarketingAllowed(),
        getDocumentCookie(window.document)
      );
      const requestPayload = buildDiagnosticRequestPayload(
        requestBase,
        this.submissionIdentity.submissionId,
        turnstileToken,
        marketingMetadata
      );
      let succeeded = false;
      this.clearTurnstileError();
      this.setSubmissionBusy(true, event.currentTarget);

      try {
        const response = await requestDiagnosticIntake({
          endpoint: this.diagnosticRequestEndpoint,
          payload: requestPayload,
          fetchImplementation: window.fetch.bind(window)
        });
        if (response.submissionIntent !== submissionIntent) {
          throw new DiagnosticSubmissionError('technical');
        }
        if (this.destroyed) return;

        this.state.authoritativeSubmissionResponse = response;
        this.state.sameSessionHandoffReady = this.persistDiagnosticHandoff(response);
        if (shouldAutoContinueToDiagnosticBooking(response, this.state.sameSessionHandoffReady)) {
          window.location.assign(DIAGNOSTIC_BOOKING_PATH);
          succeeded = true;
          return;
        }
        this.showServerSuccess(response);
        if (response.submissionIntent === 'CALLBACK_2H' && response.webQuoteConsultation) {
          window.location.assign(response.webQuoteConsultation.url);
        }
        succeeded = true;
      } catch (error) {
        if (!this.destroyed) {
          this.showSubmissionStatus(getSubmissionErrorMessage(error));
          this.resetTurnstile();
        }
      } finally {
        if (!succeeded && !this.destroyed) this.setSubmissionBusy(false);
      }
    }

    validateContact() {
      this.nodes.contactGrid?.querySelectorAll('[data-nvc-diag-contact-field]').forEach((input) => {
        const field = input.getAttribute('data-nvc-diag-contact-field');
        if (field && Object.prototype.hasOwnProperty.call(this.state.contact, field)) {
          if (input.type === 'radio' && !input.checked) return;
          this.state.contact[field] = field === 'mobile'
            ? this.sanitizeTelephoneInput(input)
            : input.value;
        }
      });
      if (this.state.contact.civility !== 'SOCIETE') this.state.contact.companyName = '';

      const errors = [];
      if (!this.isValidCivility(this.state.contact.civility)) {
        errors.push(['civility', 'Veuillez sélectionner une civilité.']);
      }
      if (this.state.contact.civility === 'SOCIETE' && !this.normalizeWhitespace(this.state.contact.companyName)) {
        errors.push(['companyName', 'Veuillez renseigner le nom de votre société']);
      }
      if (!this.normalizeWhitespace(this.state.contact.lastName)) {
        errors.push(['lastName', 'Veuillez renseigner votre nom.']);
      }
      if (!this.normalizeWhitespace(this.state.contact.firstName)) {
        errors.push(['firstName', 'Veuillez renseigner votre prénom.']);
      }
      if (!this.isValidEmail(this.state.contact.email)) {
        errors.push(['email', 'Renseignez une adresse email valide.']);
      }
      if (!this.isValidTelephone(this.state.contact.mobile)) {
        errors.push(['mobile', 'Veuillez saisir un numéro de téléphone valide.']);
      }

      ['civility', 'companyName', 'lastName', 'firstName', 'email', 'mobile'].forEach((field) => this.clearFieldError(field));
      errors.forEach(([field, message]) => this.setFieldError(field, message));

      if (this.nodes.contactErrors) this.nodes.contactErrors.hidden = errors.length === 0;
      if (!errors.length) return true;

      const firstField = errors[0][0];
      const firstControl = this.root.querySelector(`[data-nvc-diag-contact-field="${firstField}"]`);
      this.nodes.contactErrors?.focus({ preventScroll: false });
      window.requestAnimationFrame(() => firstControl?.focus({ preventScroll: false }));
      return false;
    }

    setFieldError(field, message) {
      const error = this.root.querySelector(`[data-nvc-diag-field-error="${field}"]`);
      const inputs = this.root.querySelectorAll(`[data-nvc-diag-contact-field="${field}"]`);
      const group = this.root.querySelector(`[data-nvc-diag-contact-group="${field}"]`);
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
      inputs.forEach((input) => input.setAttribute('aria-invalid', 'true'));
      group?.setAttribute('aria-invalid', 'true');
    }

    clearFieldError(field) {
      const error = this.root.querySelector(`[data-nvc-diag-field-error="${field}"]`);
      const inputs = this.root.querySelectorAll(`[data-nvc-diag-contact-field="${field}"]`);
      const group = this.root.querySelector(`[data-nvc-diag-contact-group="${field}"]`);
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
      inputs.forEach((input) => input.removeAttribute('aria-invalid'));
      group?.removeAttribute('aria-invalid');
      if (this.nodes.contactErrors) this.nodes.contactErrors.hidden = true;
    }

    isValidEmail(value) {
      const email = this.normalizeWhitespace(value);
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
    }

    isValidCivility(value) {
      return Object.prototype.hasOwnProperty.call(CONTACT_CIVILITY_LABELS, value);
    }

    isValidTelephone(value) {
      return /^\d{10}$/.test(String(value || ''));
    }

    syncFormTrustPlacement(stepIndex) {
      if (!this.nodes.formTrust || !this.nodes.formTrustHome || !this.nodes.contactTrustSlot) return;
      if (stepIndex === CONTACT_STEP_INDEX) {
        this.nodes.contactTrustSlot.append(this.nodes.formTrust);
        return;
      }
      this.nodes.formTrustHome.after(this.nodes.formTrust);
    }

    setupCertificationLogos() {
      this.root.querySelectorAll('[data-nvc-diag-certification-logo]').forEach((image) => {
        const handler = () => {
          const item = image.closest('[data-nvc-diag-certification-item]');
          if (item) item.hidden = true;
          const remaining = this.root.querySelectorAll('[data-nvc-diag-certification-item]:not([hidden])');
          if (!remaining.length && this.nodes.certifications) this.nodes.certifications.hidden = true;
        };
        image.addEventListener('error', handler, { once: true });
        this.certificationHandlers.push({ image, handler });
        if (image.complete && image.naturalWidth === 0) handler();
      });
    }

    normalizeWhitespace(value) {
      return normalizeWhitespace(value);
    }
  }

  if (IS_COMMONJS_TEST) {
    module.exports = Object.freeze({
      DIAGNOSTIC_CODES,
      DIAGNOSTIC_FORM_VERSION,
      DIAGNOSTIC_HANDOFF_STORAGE_KEY,
      DIAGNOSTIC_REQUEST_CONTRACT_VERSION,
      DiagnosticLanding,
      DiagnosticSubmissionError,
      buildDiagnosticEventSourceUrl,
      buildDiagnosticHandoff,
      buildDiagnosticMarketingMetadata,
      buildDiagnosticRequestBase,
      buildDiagnosticRequestPayload,
      canOfferOnlineBooking,
      createUuidV4,
      ensureSubmissionIdentity,
      getApplicableDiagnosticLabels,
      getBrowserAssertion,
      getSubmissionIntent,
      getSubmissionErrorMessage,
      getSuccessContent,
      normalizeDiagnosticLandingPath,
      parseDiagnosticSuccessResponse,
      postMetaEtapeAdresse,
      readAttribution,
      readMetaCookie,
      requestDiagnosticIntake,
      requiresExpertReview,
      shouldAutoContinueToDiagnosticBooking,
      stableStringify
    });
    return;
  }

  const getRoots = (scope) => {
    const roots = [];
    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    return roots;
  };

  const initializeRoot = (root) => {
    if (root.dataset.nvcDiagInitialized === 'true') return;
    const instance = new DiagnosticLanding(root);
    if (!instance.init()) return;
    root.dataset.nvcDiagInitialized = 'true';
    instances.set(root, instance);
  };

  const scan = (scope = document) => {
    getRoots(scope).forEach(initializeRoot);
  };

  const destroyWithin = (scope) => {
    getRoots(scope).forEach((root) => {
      const instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
      delete root.dataset.nvcDiagInitialized;
    });
  };

  const api = Object.freeze({ scan, destroyWithin });
  window[GLOBAL_KEY] = api;

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }
})();
